import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  setDoc,
  deleteDoc, 
  doc, 
  Timestamp, 
  orderBy,
  where,
  increment,
  limit
} from 'firebase/firestore';
import { db, createNotification, getDocs, checkInvoiceDueDates, auth, handleFirestoreError, OperationType, getDoc, withRetry, saveInvoiceWithTransaction } from '../firebase';
import { handleAsyncAction } from '../utils/AsyncActionHandler';
import { useAuth } from '../App';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Download, 
  FileDown,
  Trash2, 
  Edit2, 
  X, 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  Loader2,
  RefreshCw,
  ChevronRight,
  Receipt,
  User,
  Calendar,
  DollarSign,
  PlusCircle,
  MinusCircle,
  Mail,
  Printer,
  Eye,
  Building2,
  Wrench,
  Layout,
  UserCheck,
  Check,
  Users,
  Image as ImageIcon,
  Inbox,
  Undo
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { downloadFile } from '../lib/downloadHelper';
import { 
  Invoice, 
  InvoiceItem, 
  Client, 
  Report, 
  Settings, 
  Branch, 
  ReportSparePart, 
  Account, 
  Signatory, 
  UserProfile,
  LayoutSettings,
  CustomElement,
  TemplateSection
} from '../types';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import ConfirmationModal from '../components/ConfirmationModal';
import SuccessPopup from '../components/SuccessPopup';

const calculateSubtotal = (items: InvoiceItem[]) => {
  return items.reduce((sum, item) => {
    const amt = typeof item.amount === 'string' ? (item.amount === '' ? 0 : Number(item.amount)) : item.amount;
    return sum + (isNaN(amt as number) ? 0 : (amt as number));
  }, 0);
};

const calculateVatAmount = (items: InvoiceItem[], rate: number) => {
  return items.reduce((sum, item) => {
    if (item.vatStatus === 'No VAT') return sum;
    const amt = typeof item.amount === 'string' ? (item.amount === '' ? 0 : Number(item.amount)) : (item.amount || 0);
    return sum + (isNaN(Number(amt)) ? 0 : (Number(amt) * rate / 100));
  }, 0);
};

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, isAdmin, isManager, isBilling, isBillingHOD, isAccountantHOD, isAccountant, isCashier } = useAuth();

  const adjustAccountBalance = async (
    oldStatus: Invoice['status'] | undefined,
    newStatus: Invoice['status'],
    oldBankAccountId: string | undefined | null,
    newBankAccountId: string | undefined | null,
    oldTotalAmount: number | undefined,
    newTotalAmount: number
  ) => {
    try {
      // Case 1: Status is changing to 'paid'
      if (oldStatus !== 'paid' && newStatus === 'paid') {
        if (newBankAccountId) {
          await updateDoc(doc(db, 'accounts', newBankAccountId), {
            balance: increment(newTotalAmount)
          });
        }
      }
      // Case 2: Status is changing FROM 'paid' to something else
      else if (oldStatus === 'paid' && newStatus !== 'paid') {
        if (oldBankAccountId) {
          await updateDoc(doc(db, 'accounts', oldBankAccountId), {
            balance: increment(-(oldTotalAmount || 0))
          });
        }
      }
      // Case 3: Status remains 'paid', but bankAccountId changed and/or totalAmount changed
      else if (oldStatus === 'paid' && newStatus === 'paid') {
        if (oldBankAccountId === newBankAccountId) {
          const diff = newTotalAmount - (oldTotalAmount || 0);
          if (diff !== 0 && newBankAccountId) {
            await updateDoc(doc(db, 'accounts', newBankAccountId), {
              balance: increment(diff)
            });
          }
        } else {
          if (oldBankAccountId) {
            await updateDoc(doc(db, 'accounts', oldBankAccountId), {
              balance: increment(-(oldTotalAmount || 0))
            });
          }
          if (newBankAccountId) {
            await updateDoc(doc(db, 'accounts', newBankAccountId), {
              balance: increment(newTotalAmount)
            });
          }
        }
      }
    } catch (error) {
      console.error('Error adjusting account balance:', error);
    }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const reportIdParam = searchParams.get('reportId');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [showList, setShowList] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [approvedReports, setApprovedReports] = useState<Report[]>([]);
  const [letterheadUrl, setLetterheadUrl] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [defaultCurrency, setDefaultCurrency] = useState('TZS');
  const [vatRate, setVatRate] = useState(18);
  const [proformaInvoiceStart, setProformaInvoiceStart] = useState(26313);
  const [taxInvoiceStart, setTaxInvoiceStart] = useState(1);
  const [invoiceStart, setInvoiceStart] = useState(1);
  const [loading, setLoading] = useState(true);
  const [invoiceLimit, setInvoiceLimit] = useState(25);
  const [hasMoreInvoices, setHasMoreInvoices] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [signatories, setSignatories] = useState<Signatory[]>([]);
  
  // Pending Box / Draft states
  const [pendingDrafts, setPendingDrafts] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('pending_invoice_drafts');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Failed to parse pending drafts:', e);
      return [];
    }
  });
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [isPendingBoxOpen, setIsPendingBoxOpen] = useState(false);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPartSelectorOpen, setIsPartSelectorOpen] = useState(false);
  const [partSearchTerm, setPartSearchTerm] = useState('');
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [bankAccounts, setBankAccounts] = useState<Account[]>([]);
  const [attachSurveyReport, setAttachSurveyReport] = useState(true);
  const [emailData, setEmailData] = useState({
    to: '',
    cc: '',
    subject: '',
    body: '',
    invoiceId: ''
  });
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invoiceSuccessMessage, setInvoiceSuccessMessage] = useState<string | null>(null);
  const [clientBranches, setClientBranches] = useState<Branch[]>([]);
  const [isBranchLoading, setIsBranchLoading] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [clientSpareParts, setClientSpareParts] = useState<ReportSparePart[]>([]);
  const [billingUsers, setBillingUsers] = useState<UserProfile[]>([]);
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [isAgentFilterOpen, setIsAgentFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'tax' | 'proforma'>('tax');
  const [convertingFromProformaId, setConvertingFromProformaId] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmLabel?: string;
    cancelLabel?: string;
    isDanger?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  
  const [bankAccountSelectInvoice, setBankAccountSelectInvoice] = useState<Invoice | null>(null);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>('');
  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>({
    fontSize: 11,
    fontFamily: 'helvetica',
    fontStyle: 'normal',
    logoWidth: 35,
    marginTop: 12,
    marginBottom: 7,
    marginLeft: 15,
    marginRight: 15,
    headerHeight: 35,
    infoBoxesY: 45,
    descriptionStartY: 80,
    itemsStartY: 0,
    footerY: 238,
    footerHeight: 52,
    signatureHeight: 18,
    itemsRowHeight: 4.4,
    columnWidths: {
      sn: 15,
      qty: 15,
      unit: 15,
      rate: 25,
      amount: 25
    },
    customElements: []
  });

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    clientId: '',
    branchId: '',
    reportId: '',
    serviceType: '',
    items: [{ serialNumber: '', description: '', quantity: '', unit: '', unitPrice: '', amount: '' }] as InvoiceItem[],
    description: '',
    notes: '',
    type: 'formal' as Invoice['type'],
    template: 'standard' as Invoice['template'],
    bankAccountId: '',
    signatoryName: '',
    signatoryTitle: '',
    signatureUrl: '',
    salesAgent: '',
    agentPhone: '',
    smNumber: '',
    lpoNumber: '',
    deliveryNote: '',
    status: 'draft' as Invoice['status'],
    dueDate: new Date().toISOString().split('T')[0],
    fontSize: 11,
    fontFamily: 'helvetica',
    fontStyle: 'normal'
  });

  const [billForGeneralService, setBillForGeneralService] = useState(false);

  const selectedClient = clients.find(c => c.id === formData.clientId);
  const isServiceClient = selectedClient?.serviceType?.toLowerCase().includes('service');
  const hasGeneralService = selectedClient?.generalServiceItems && selectedClient.generalServiceItems.length > 0;

  const handleToggleGeneralService = (checked: boolean) => {
    setBillForGeneralService(checked);
    if (checked && selectedClient?.generalServiceItems) {
      const mappedItems = selectedClient.generalServiceItems.map((item, index) => ({
        serialNumber: (index + 1).toString(),
        description: item.description,
        quantity: item.quantity,
        unit: 'pcs',
        unitPrice: item.unitPrice,
        amount: item.quantity * item.unitPrice,
        vatStatus: 'VAT' as const
      }));
      setFormData(prev => ({
        ...prev,
        items: mappedItems
      }));
      toast.success('Populated invoice with client general service pricing');
    } else {
      setFormData(prev => ({
        ...prev,
        items: [{ serialNumber: '', description: '', quantity: '', unit: '', unitPrice: '', amount: '', vatStatus: 'VAT' }]
      }));
    }
  };

  // Form state

  const descriptionRef = React.useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (descriptionRef.current) {
      descriptionRef.current.style.height = 'auto';
      descriptionRef.current.style.height = `${descriptionRef.current.scrollHeight}px`;
    }
  }, [formData.description]);

  useEffect(() => {
    if (!formData.clientId) {
      setClientBranches([]);
      setClientSpareParts([]);
      return;
    }

    setIsBranchLoading(true);

    // 1. Branches Listener
    const branchesRef = collection(db, 'clients', formData.clientId, 'branches');
    const unsubBranches = onSnapshot(branchesRef, (snapshot) => {
      const branchesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Branch));
      setClientBranches(branchesData);
      setIsBranchLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, `clients/${formData.clientId}/branches`);
      setIsBranchLoading(false);
    });

    // 2. Sparse Parts Listeners Tracker
    let reportParts: ReportSparePart[] = [];
    let clientArrayParts: ReportSparePart[] = [];
    let subcollectionParts: ReportSparePart[] = [];

    const syncFinalParts = () => {
      const combined: ReportSparePart[] = [...reportParts];
      
      const addToCombined = (p: ReportSparePart) => {
        if (!combined.find(ex => ex.name.trim().toLowerCase() === p.name.trim().toLowerCase())) {
          combined.push(p);
        }
      };

      clientArrayParts.forEach(addToCombined);
      subcollectionParts.forEach(addToCombined);

      setClientSpareParts(combined.sort((a, b) => a.name.localeCompare(b.name)));
    };

    // 2a. Reports Query
    const reportsQuery = query(collection(db, 'reports'), where('clientId', '==', formData.clientId));
    const unsubReports = onSnapshot(reportsQuery, (snapshot) => {
      const parts: ReportSparePart[] = [];
      snapshot.docs.forEach(doc => {
        const report = doc.data() as Report;
        report.requiredSpareParts?.forEach(p => {
          if (!parts.find(ex => ex.name === p.name)) parts.push(p);
        });
      });
      reportParts = parts;
      syncFinalParts();
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'reports');
    });

    // 2b. Client Document Array
    const clientRef = doc(db, 'clients', formData.clientId);
    const unsubClient = onSnapshot(clientRef, (snap) => {
      const data = snap.data() as Client;
      clientArrayParts = (data?.spareParts || []).map(p => ({
        name: p.name,
        quantity: 1, // Default for selector
        price: p.price,
        unit: p.unit
      }));
      syncFinalParts();
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, `clients/${formData.clientId}`);
    });

    // 2c. Dedicated Subcollection
    const sparePartsRef = collection(db, 'clients', formData.clientId, 'spareParts');
    const unsubSubcollection = onSnapshot(sparePartsRef, (snap) => {
      subcollectionParts = snap.docs.map(doc => doc.data() as ReportSparePart);
      syncFinalParts();
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, `clients/${formData.clientId}/spareParts`);
    });

    return () => {
      unsubBranches();
      unsubReports();
      unsubClient();
      unsubSubcollection();
    };
  }, [formData.clientId]);

  useEffect(() => {
    if (!profile) return;
    checkInvoiceDueDates();
    
    // Isolation: Admin, Manager, and Billing/Finance HODs see all. 
    // Billing users see their own and invoices created by other Billing users.
    // Others see only their own.
    const canSeeAll = isAdmin || isManager || isBillingHOD || isAccountantHOD;
    let q;
    if (canSeeAll) {
      q = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'), limit(invoiceLimit));
    } else if (isBilling) {
      q = query(collection(db, 'invoices'), where('creatorRole', '==', 'billing'), orderBy('createdAt', 'desc'), limit(invoiceLimit));
    } else {
      q = query(collection(db, 'invoices'), where('creatorId', '==', profile.uid), orderBy('createdAt', 'desc'), limit(invoiceLimit));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const invoicesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Invoice));
      setInvoices(invoicesData);
      setHasMoreInvoices(snapshot.docs.length >= invoiceLimit);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'invoices');
    });

    const clientsUnsubscribe = onSnapshot(collection(db, 'clients'), (snapshot) => {
      setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clients');
    });

    const reportsQuery = query(
      collection(db, 'reports'), 
      where('status', 'in', ['approved', 'submitted_to_billing', 'technical_survey_report'])
    );
    const reportsUnsubscribe = onSnapshot(reportsQuery, (snapshot) => {
      setApprovedReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'reports');
    });

    const settingsUnsubscribe = onSnapshot(collection(db, 'settings'), (snapshot) => {
      if (!snapshot.empty) {
        // Try to find a document named 'config' or 'settings' or just take the first one that has letterheadUrl
        const configDoc = snapshot.docs.find(d => d.id === 'config') || 
                          snapshot.docs.find(d => d.data().letterheadUrl) || 
                          snapshot.docs[0];
        const settings = configDoc.data() as Settings;
        setLetterheadUrl(settings.letterheadUrl || null);
        setLogoUrl(settings.logoUrl || null);
        setSignatureUrl(settings.signatureUrl || null);
        setDefaultCurrency(settings.defaultCurrency || 'TZS');
        setVatRate(settings.vatRate ?? 18);
        setProformaInvoiceStart(settings.proformaInvoiceStart ?? 26313);
        setTaxInvoiceStart(settings.taxInvoiceStart ?? 1);
        setInvoiceStart(settings.invoiceStart ?? 1);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'settings');
    });

    const bankAccountsUnsubscribe = onSnapshot(query(collection(db, 'accounts'), where('type', 'in', ['bank', 'cash', 'Mobile Money', 'other'])), (snapshot) => {
      setBankAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'accounts');
    });

    const signatoriesUnsubscribe = onSnapshot(collection(db, 'signatories'), (snapshot) => {
      setSignatories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Signatory)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'signatories');
    });

    const brandingUnsubscribe = onSnapshot(doc(db, 'settings', 'branding'), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setLetterheadUrl(data.letterheadUrl || null);
        setLogoUrl(data.logoUrl || null);
        setSignatureUrl(data.signatureUrl || null);
      }
    });

    const layoutUnsubscribe = onSnapshot(doc(db, 'settings', 'invoiceLayout'), (docSnap) => {
      if (docSnap.exists()) {
        setLayoutSettings(prev => ({ ...prev, ...docSnap.data() }));
      }
    });

    // Fetch Billing Users for filter
    const billingUsersQuery = query(collection(db, 'users'), where('role', 'in', ['billing', 'admin', 'manager', 'accountant']));
    const billingUsersUnsubscribe = onSnapshot(billingUsersQuery, (snapshot) => {
      setBillingUsers(snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile)));
    });

    // Fetch all potential creators for name mapping
    const allUsersUnsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const map: Record<string, string> = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        map[doc.id] = data.displayName || data.email || 'Unknown';
      });
      setUsersMap(map);
    });

    return () => {
      unsubscribe();
      clientsUnsubscribe();
      reportsUnsubscribe();
      settingsUnsubscribe();
      bankAccountsUnsubscribe();
      signatoriesUnsubscribe();
      brandingUnsubscribe();
      layoutUnsubscribe();
      billingUsersUnsubscribe();
      allUsersUnsubscribe();
    };
  }, [profile]);

  useEffect(() => {
    if (reportIdParam && approvedReports.length > 0 && !isModalOpen && !editingInvoice) {
      const report = approvedReports.find(r => r.id === reportIdParam);
      if (report) {
        openModal(undefined, reportIdParam);
        // Clear param after opening
        setSearchParams({});
      }
    }
  }, [reportIdParam, approvedReports, isModalOpen, editingInvoice]);

  useEffect(() => {
    if (isSubmitting || !isModalOpen || !editingInvoice && !currentDraftId) return;
    if (isModalOpen && !editingInvoice && currentDraftId && !isSubmitting) {
      const hasData = formData.clientId !== '' || 
                      formData.items.some(item => item.description !== '' || (item.unitPrice !== '' && Number(item.unitPrice) > 0)) || 
                      formData.notes !== '' || 
                      formData.description !== '';
      
      if (hasData) {
        const clientName = clients.find(c => c.id === formData.clientId)?.name || 'Unnamed Client';
        const existing = pendingDrafts.find(d => d.id === currentDraftId);
        const draftItem = {
          id: currentDraftId,
          userId: profile?.uid || '',
          invoiceNumber: formData.invoiceNumber,
          clientId: formData.clientId,
          clientName,
          type: formData.type,
          updatedAt: new Date().toISOString(),
          createdAt: existing?.createdAt || new Date().toISOString(),
          formData
        };
        
        setPendingDrafts(prev => {
          const index = prev.findIndex(d => d.id === currentDraftId);
          let updated;
          if (index >= 0) {
            updated = [...prev];
            updated[index] = draftItem;
          } else {
            updated = [draftItem, ...prev];
          }
          localStorage.setItem('pending_invoice_drafts', JSON.stringify(updated));
          setTimeout(() => {
            window.dispatchEvent(new Event('pending_drafts_updated'));
          }, 0);
          return updated;
        });
      }
    }
  }, [formData, isModalOpen, editingInvoice, currentDraftId, clients, profile]);

  // Periodic active draft auto-save to Firestore if online
  useEffect(() => {
    if (!profile || !isModalOpen || editingInvoice || !currentDraftId || isSubmitting) return;

    let timer: NodeJS.Timeout | null = null;

    const saveActiveDraftToFirestore = async () => {
      if (!navigator.onLine) return;

      const hasData = formData.clientId !== '' || 
                      formData.items.some(item => item.description !== '' || (item.unitPrice !== '' && Number(item.unitPrice) > 0)) || 
                      formData.notes !== '' || 
                      formData.description !== '';
      
      if (!hasData) return;

      const clientName = clients.find(c => c.id === formData.clientId)?.name || 'Unnamed Client';
      const existing = pendingDrafts.find(d => d.id === currentDraftId);
      const draftItem = {
        id: currentDraftId,
        userId: profile.uid,
        invoiceNumber: formData.invoiceNumber,
        clientId: formData.clientId,
        clientName,
        type: formData.type,
        updatedAt: new Date().toISOString(),
        createdAt: existing?.createdAt || new Date().toISOString(),
        formData
      };

      try {
        await setDoc(doc(db, 'pending_invoice_drafts', currentDraftId), draftItem);
        console.log('Periodic auto-save draft to Firestore successful');
      } catch (err) {
        console.error('Periodic auto-save draft to Firestore failed:', err);
      }
    };

    timer = setInterval(saveActiveDraftToFirestore, 5000);

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [formData, isModalOpen, editingInvoice, currentDraftId, profile, clients, pendingDrafts]);

  // Two-way synchronization of pending drafts with Firestore & local storage
  useEffect(() => {
    if (!profile) return;

    // Filter out drafts older than 7 days (7 * 24 * 60 * 60 * 1000 = 604800000 ms)
    const EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000;
    const isExpired = (createdAtString: string) => {
      if (!createdAtString) return false;
      const createdTime = new Date(createdAtString).getTime();
      return (Date.now() - createdTime) > EXPIRATION_MS;
    };

    const q = query(
      collection(db, 'pending_invoice_drafts'),
      where('userId', '==', profile.uid)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const firestoreDrafts = snapshot.docs.map(doc => doc.data());
      
      let localDrafts: any[] = [];
      try {
        const saved = localStorage.getItem('pending_invoice_drafts');
        localDrafts = saved ? JSON.parse(saved) : [];
      } catch (e) {
        console.error('Failed to parse local drafts:', e);
      }

      // Filter expired drafts from local and firestore
      const activeFirestoreDrafts = firestoreDrafts.filter(d => !isExpired(d.createdAt || d.updatedAt));
      const expiredFirestoreDrafts = firestoreDrafts.filter(d => isExpired(d.createdAt || d.updatedAt));

      // Delete expired drafts from Firestore
      for (const d of expiredFirestoreDrafts) {
        try {
          await deleteDoc(doc(db, 'pending_invoice_drafts', d.id));
        } catch (err) {
          console.error('Failed to delete expired draft from Firestore:', err);
        }
      }

      const mergedMap = new Map<string, any>();

      // Populate local drafts first if they are not expired
      localDrafts.forEach(ld => {
        if (!isExpired(ld.createdAt || ld.updatedAt)) {
          mergedMap.set(ld.id, ld);
        }
      });

      // Merge Firestore drafts
      for (const fd of activeFirestoreDrafts) {
        const ld = mergedMap.get(fd.id);
        if (!ld) {
          // Exists on Firestore but not locally -> Download
          mergedMap.set(fd.id, fd);
        } else {
          // Exists in both -> Compare updatedAt
          const fdTime = new Date(fd.updatedAt).getTime();
          const ldTime = new Date(ld.updatedAt).getTime();
          if (fdTime > ldTime) {
            // Firestore is newer -> Download
            mergedMap.set(fd.id, fd);
          } else if (ldTime > fdTime) {
            // Local is newer -> Upload to Firestore
            if (navigator.onLine) {
              try {
                await setDoc(doc(db, 'pending_invoice_drafts', ld.id), ld);
              } catch (err) {
                console.error('Failed to sync newer local draft to Firestore:', err);
              }
            }
          }
        }
      }

      // Check for local drafts that don't exist in Firestore and upload them
      for (const ld of localDrafts) {
        if (isExpired(ld.createdAt || ld.updatedAt)) continue;
        const existsInFirestore = activeFirestoreDrafts.some(fd => fd.id === ld.id);
        if (!existsInFirestore && navigator.onLine) {
          try {
            const draftToSync = { ...ld, userId: profile.uid };
            await setDoc(doc(db, 'pending_invoice_drafts', ld.id), draftToSync);
          } catch (err) {
            console.error('Failed to upload local draft to Firestore:', err);
          }
        }
      }

      const mergedList = Array.from(mergedMap.values()).sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );

      const currentSavedStr = localStorage.getItem('pending_invoice_drafts') || '[]';
      const newStr = JSON.stringify(mergedList);
      if (currentSavedStr !== newStr) {
        localStorage.setItem('pending_invoice_drafts', newStr);
        setPendingDrafts(mergedList);
        setTimeout(() => {
          window.dispatchEvent(new Event('pending_drafts_updated'));
        }, 0);
      }
    }, (error) => {
      console.error('Error listening to pending drafts:', error);
    });

    return () => unsubscribe();
  }, [profile]);

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { serialNumber: '', description: '', quantity: '', unit: '', unitPrice: '', amount: '', vatStatus: 'VAT' }]
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      if (!newItems[index]) return prev;

      const val = (field === 'quantity' || field === 'unitPrice') && (value === '' || isNaN(value)) ? '' : value;
      newItems[index] = { ...newItems[index], [field]: val };
      
      // Auto-fill unit if a part is selected from clientSpareParts
      if (field === 'description') {
        const selectedPart = clientSpareParts.find(p => p.name === value);
        if (selectedPart) {
          newItems[index] = { 
            ...newItems[index], 
            unitPrice: selectedPart.price || '',
            unit: selectedPart.unit || 'pc'
          };
        }
      }

      if (field === 'description' || field === 'quantity' || field === 'unitPrice') {
        const q = newItems[index].quantity === '' ? 0 : Number(newItems[index].quantity);
        const p = newItems[index].unitPrice === '' ? 0 : Number(newItems[index].unitPrice);
        newItems[index].amount = (q === 0 && p === 0 && newItems[index].description === '') ? '' : q * p;
      }
      
      return { ...prev, items: newItems };
    });
  };

  const handleReportSelect = (reportId: string) => {
    const report = approvedReports.find(r => r.id === reportId);
    if (report) {
      // Auto-populate from report
      const reportItems: InvoiceItem[] = [
        { 
          description: `Technical ${report.type.toUpperCase()} Report - Ref: ${report.id.substring(0, 8)}`, 
          quantity: 1, 
          unitPrice: 0, 
          amount: 0,
          unit: 'pc'
        }
      ];

      // Add spare parts if any
      if (report.requiredSpareParts && report.requiredSpareParts.length > 0) {
        report.requiredSpareParts.forEach(part => {
          reportItems.push({
            description: part.name,
            quantity: part.quantity,
            unit: part.unit || 'pc',
            unitPrice: 0, // Needs pricing by billing
            amount: 0
          });
        });
      }

      setFormData({
        ...formData,
        reportId,
        clientId: report.clientId,
        type: report.type === 'survey' ? 'proforma' : 'formal',
        items: reportItems.length > 0 ? reportItems : formData.items
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || (!isAdmin && !isManager && !isBilling && !isBillingHOD && !isAccountantHOD && !isAccountant && !isCashier)) {
      toast.error('You do not have permission to perform this action');
      return;
    }

    await handleAsyncAction(async () => {
      const subtotal = calculateSubtotal(formData.items);
      const vatAmount = calculateVatAmount(formData.items, vatRate);
      const totalAmount = subtotal + vatAmount;
      const client = clients.find(c => c.id === formData.clientId);
      const branch = clientBranches.find(b => b.id === formData.branchId);
      const currentUser = auth.currentUser;

      const invoiceData: any = {
        invoiceNumber: formData.invoiceNumber || getNextInvoiceNumber(),
        clientId: formData.clientId || 'guest',
        clientName: client?.name || 'Unknown Client',
        branchId: formData.branchId || null,
        branchName: branch?.name || '',
        currency: client?.currency || defaultCurrency,
        reportId: formData.reportId || null,
        serviceType: formData.serviceType || '',
        items: formData.items.map(item => ({
          ...item,
          quantity: item.quantity === '' ? 0 : Number(item.quantity),
          unitPrice: item.unitPrice === '' ? 0 : Number(item.unitPrice),
          amount: item.amount === '' ? 0 : Number(item.amount),
          vatStatus: (item as any).vatStatus || 'VAT'
        })),
        description: formData.description || '',
        notes: formData.notes || '',
        vatRate,
        vatAmount,
        totalAmount,
        type: formData.type,
        template: formData.template,
        salesAgent: formData.salesAgent || '',
        agentPhone: formData.agentPhone || '',
        smNumber: formData.smNumber || '',
        lpoNumber: formData.lpoNumber || '',
        deliveryNote: formData.deliveryNote || '',
        bankAccountId: formData.bankAccountId || null,
        signatoryName: formData.signatoryName || '',
        signatoryTitle: formData.signatoryTitle || '',
        signatureUrl: formData.signatureUrl || '',
        fontSize: formData.fontSize || 11,
        fontFamily: formData.fontFamily || 'helvetica',
        fontStyle: formData.fontStyle || 'normal',
        status: formData.status,
        dueDate: formData.dueDate ? Timestamp.fromDate(new Date(formData.dueDate)) : Timestamp.now(),
        createdAt: editingInvoice ? editingInvoice.createdAt : Timestamp.now(),
        creatorId: editingInvoice ? (editingInvoice as any).creatorId || currentUser?.uid || '' : currentUser?.uid || '',
        creatorEmail: editingInvoice ? (editingInvoice as any).creatorEmail || currentUser?.email || '' : currentUser?.email || '',
        creatorName: editingInvoice ? (editingInvoice as any).creatorName || profile?.displayName || 'Unknown' : profile?.displayName || 'Unknown',
        creatorRole: editingInvoice ? (editingInvoice as any).creatorRole || profile?.role || 'user' : profile?.role || 'user'
      };

      if (editingInvoice) {
        invoiceData.updatedAt = Timestamp.now();
      }

      // Strip undefined values to prevent Firestore SDK errors
      Object.keys(invoiceData).forEach(key => {
        if (invoiceData[key] === undefined) {
          delete invoiceData[key];
        }
      });
      
      invoiceData.items = invoiceData.items.map((item: any) => {
        const cleanItem = { ...item };
        Object.keys(cleanItem).forEach(k => {
          if (cleanItem[k] === undefined) delete cleanItem[k];
        });
        return cleanItem;
      });

      console.log('Submitting invoice data:', invoiceData);
      
      // Save new spare parts to client's dedicated list
      if (formData.clientId) {
        const sparePartsRef = collection(db, 'clients', formData.clientId, 'spareParts');
        for (const item of formData.items) {
          const exists = clientSpareParts.find(p => p.name.toLowerCase() === item.description.toLowerCase());
          if (!exists && item.description.trim()) {
            await withRetry(async () => {
              await addDoc(sparePartsRef, {
                name: item.description,
                price: item.unitPrice,
                unit: item.unit || '',
                createdAt: Timestamp.now()
              });
            });
          }
        }
      }

      if (editingInvoice) {
        await adjustAccountBalance(
          editingInvoice.status,
          invoiceData.status,
          editingInvoice.bankAccountId,
          invoiceData.bankAccountId,
          editingInvoice.totalAmount,
          invoiceData.totalAmount
        );
        await saveInvoiceWithTransaction(invoiceData, editingInvoice.id!);
        setInvoiceSuccessMessage('Invoice updated successfully');
      } else {
        await adjustAccountBalance(
          undefined,
          invoiceData.status,
          undefined,
          invoiceData.bankAccountId,
          0,
          invoiceData.totalAmount
        );
        await saveInvoiceWithTransaction(invoiceData);
        
        // If converting from Proforma, update the source Proforma with a link note
        if (convertingFromProformaId && invoiceData.type === 'tax') {
          const proformaRef = doc(db, 'invoices', convertingFromProformaId);
          await withRetry(async () => {
            await updateDoc(proformaRef, {
              billedUnderInvoiceNo: invoiceData.invoiceNumber,
              updatedAt: Timestamp.now()
            });
          });
          setConvertingFromProformaId(null);
        }
        
        // Notify managers and admins
        try {
          const qManagers = query(collection(db, 'users'), where('role', 'in', ['admin', 'manager']));
          const managers = await getDocs(qManagers);
          for (const manager of managers.docs) {
            await createNotification(
              manager.id,
              'New Invoice Generated',
              `A new invoice #${formData.invoiceNumber} has been generated for ${client?.name || 'a client'}.`,
              'invoice_generated',
              '/invoices'
            );
          }
        } catch (notifErr) {
          console.warn('Failed to send notifications:', notifErr);
        }
        
        setInvoiceSuccessMessage('Invoice created successfully');
      }
      
      if (!editingInvoice && currentDraftId) {
        setPendingDrafts(prev => {
          const updated = prev.filter(d => d.id !== currentDraftId);
          localStorage.setItem('pending_invoice_drafts', JSON.stringify(updated));
          setTimeout(() => {
            window.dispatchEvent(new Event('pending_drafts_updated'));
          }, 0);
          return updated;
        });
        if (profile && navigator.onLine) {
          try {
            await withRetry(async () => {
              await deleteDoc(doc(db, 'pending_invoice_drafts', currentDraftId));
            });
          } catch (err) {
            console.error('Failed to delete submitted draft from Firestore:', err);
          }
        }
        setCurrentDraftId(null);
      }
      setIsPendingBoxOpen(false);
      closeModal();
    }, setIsSubmitting, {
      successMessage: editingInvoice ? 'Invoice updated successfully' : 'Invoice created successfully',
      errorMessage: 'Failed to save invoice',
      onError: (error) => {
        handleFirestoreError(error, editingInvoice ? OperationType.UPDATE : OperationType.CREATE, 'invoices');
      }
    });
  };

  const handleDuplicateInvoice = (invoice: Invoice) => {
    const { id, createdAt, updatedAt, ...rest } = invoice as any;
    setEditingInvoice(null);
    setFormData({
      ...rest,
      invoiceNumber: getNextInvoiceNumber(), // Still give it a new number by default
      status: 'draft',
      dueDate: new Date().toISOString().split('T')[0]
    });
    setIsModalOpen(true);
    toast.info('Duplicating invoice... You can change details before saving.');
  };

  const handleRestoreAsProforma = async (invoice: Invoice) => {
    setConfirmModal({
      isOpen: true,
      title: 'Restore as Proforma',
      message: `This will create a NEW Proforma Invoice with the same details as #${invoice.invoiceNumber}. Keep original Tax Invoice?`,
      confirmLabel: 'Yes, Restore',
      onConfirm: async () => {
        try {
          const { id, createdAt, updatedAt, billedUnderInvoiceNo, ...rest } = invoice as any;
          await addDoc(collection(db, 'invoices'), {
            ...rest,
            type: 'proforma',
            status: 'draft',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            creatorId: profile?.uid,
            creatorEmail: profile?.email,
            creatorName: profile?.displayName,
            creatorRole: profile?.role
          });
          toast.success('Proforma restored successfully into the list');
        } catch (error) {
          handleFirestoreError(error, OperationType.CREATE, 'invoices');
          toast.error('Failed to restore proforma');
        }
      }
    });
  };

  const handleDelete = async (id: string) => {
    const invoice = invoices.find(inv => inv.id === id);
    setConfirmModal({
      isOpen: true,
      title: 'Delete Invoice',
      message: 'Are you sure you want to delete this invoice? This action cannot be undone.',
      onConfirm: async () => {
        try {
          if (invoice && invoice.status === 'paid' && invoice.bankAccountId) {
            await adjustAccountBalance(
              'paid',
              'cancelled',
              invoice.bankAccountId,
              invoice.bankAccountId,
              invoice.totalAmount,
              0
            );
          }
          await deleteDoc(doc(db, 'invoices', id));
          toast.success('Invoice deleted');
        } catch (error) {
          toast.error('Failed to delete invoice');
        }
      }
    });
  };

  const safeToDate = (val: any): Date => {
    if (!val) return new Date();
    if (typeof val.toDate === 'function') return val.toDate();
    if (val instanceof Date) return val;
    const d = new Date(val);
    return isNaN(d.getTime()) ? new Date() : d;
  };

  const formatLongDate = (date: Date | Timestamp | string) => {
    let d: Date;
    if (date instanceof Timestamp) {
      d = date.toDate();
    } else if (typeof date === 'string') {
      d = new Date(date);
    } else {
      d = date;
    }
    
    if (isNaN(d.getTime())) return 'N/A';
    
    const day = d.getDate();
    const month = d.toLocaleDateString('en-GB', { month: 'long' });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getNextInvoiceNumber = () => {
    // Find the maximum numeric part of any existing invoice numbers (all types)
    const invoiceNumbers = invoices
      .map(i => {
        // Extract all digits from the invoice number
        const match = i.invoiceNumber.match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
      });
    
    // We use the highest of invoiceStart, proformaInvoiceStart, taxInvoiceStart as the shared baseline
    const baseline = Math.max(invoiceStart, proformaInvoiceStart || 0, taxInvoiceStart || 0);
    
    const maxNumber = Math.max(baseline - 1, ...invoiceNumbers, 0);
    const nextNumber = maxNumber + 1;
    
    // Return just the numbers, padded to 5 digits
    return nextNumber.toString().padStart(5, '0');
  };

  const openModal = (invoice?: Invoice, reportId?: string) => {
    setIsSubmitting(false);
    setBillForGeneralService(false);
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    const defaultTaxDueDate = d.toISOString().split('T')[0];

    const p = new Date();
    p.setMonth(p.getMonth() + 2);
    const defaultProformaDueDate = p.toISOString().split('T')[0];

    if (invoice) {
      setEditingInvoice(invoice);
      setCurrentDraftId(null);
      setFormData({
        invoiceNumber: invoice.invoiceNumber,
        clientId: invoice.clientId,
        branchId: invoice.branchId || '',
        reportId: invoice.reportId || '',
        serviceType: invoice.serviceType || '',
        items: invoice.items.map(item => ({ ...item, vatStatus: item.vatStatus || 'VAT' })),
        description: invoice.description || '',
        notes: invoice.notes || '',
        type: invoice.type || 'formal',
        template: invoice.template || 'standard',
        bankAccountId: invoice.bankAccountId || '',
        signatoryName: invoice.signatoryName || '',
        signatoryTitle: invoice.signatoryTitle || '',
        signatureUrl: invoice.signatureUrl || '',
        salesAgent: invoice.salesAgent || '',
        agentPhone: invoice.agentPhone || '',
        smNumber: invoice.smNumber || '',
        lpoNumber: invoice.lpoNumber || '',
        deliveryNote: invoice.deliveryNote || '',
        status: invoice.status,
        dueDate: safeToDate(invoice.dueDate).toISOString().split('T')[0],
        fontSize: invoice.fontSize || 11,
        fontFamily: invoice.fontFamily || 'helvetica',
        fontStyle: invoice.fontStyle || 'normal'
      });
    } else {
      setEditingInvoice(null);
      setCurrentDraftId('draft_' + Date.now());
      const initialFormData = {
        invoiceNumber: getNextInvoiceNumber(),
        clientId: '',
        branchId: '',
        reportId: '',
        serviceType: '',
        items: [{ serialNumber: '', description: '', quantity: '', unit: '', unitPrice: '', amount: '', vatStatus: 'VAT' }] as InvoiceItem[],
        description: '',
        notes: '',
        type: (activeTab === 'proforma' ? 'proforma' : 'tax') as 'formal' | 'proforma' | 'tax',
        template: 'standard' as 'standard' | 'modern' | 'classic',
        bankAccountId: '',
        signatoryName: profile?.displayName || '',
        signatoryTitle: profile?.role || '',
        signatureUrl: signatureUrl || '',
        salesAgent: profile?.displayName?.toUpperCase() || '',
        agentPhone: profile?.phone || '',
        smNumber: '',
        lpoNumber: '',
        deliveryNote: '',
        status: 'draft' as const,
        dueDate: activeTab === 'proforma' ? defaultProformaDueDate : defaultTaxDueDate,
        fontSize: 11,
        fontFamily: 'helvetica',
        fontStyle: 'normal'
      };

      if (reportId) {
        const report = approvedReports.find(r => r.id === reportId);
        if (report) {
          initialFormData.reportId = reportId;
          initialFormData.clientId = report.clientId;
          initialFormData.type = report.type === 'survey' ? 'proforma' : 'formal';
          
          // Use type-specific numbering
          initialFormData.invoiceNumber = getNextInvoiceNumber();
          
          if (initialFormData.type === 'proforma') {
            initialFormData.dueDate = defaultProformaDueDate;
          } else {
            initialFormData.dueDate = defaultTaxDueDate;
          }
          initialFormData.items = [
            { 
              serialNumber: '',
              description: `Technical Survey Report: ${report.type.toUpperCase()} - ${report.id.substring(0, 8)}`, 
              quantity: 1, 
              unit: '',
              unitPrice: 0, 
              amount: 0,
              vatStatus: 'VAT'
            }
          ];
        }
      }
      setFormData(initialFormData);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsSubmitting(false);
    setIsModalOpen(false);
    setEditingInvoice(null);
    setConvertingFromProformaId(null);
    setBillForGeneralService(false);
  };

  const resumeDraft = (draft: any) => {
    setCurrentDraftId(draft.id);
    setFormData(draft.formData);
    setEditingInvoice(null);
    setIsModalOpen(true);
    setIsPendingBoxOpen(false);
    toast.success('Draft resumed successfully');
  };

  const deleteDraft = async (draftId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDrafts(prev => {
      const updated = prev.filter(d => d.id !== draftId);
      localStorage.setItem('pending_invoice_drafts', JSON.stringify(updated));
      setTimeout(() => {
        window.dispatchEvent(new Event('pending_drafts_updated'));
      }, 0);
      return updated;
    });
    if (currentDraftId === draftId) {
      setCurrentDraftId(null);
    }
    if (profile && navigator.onLine) {
      try {
        await deleteDoc(doc(db, 'pending_invoice_drafts', draftId));
      } catch (err) {
        console.error('Failed to delete draft from Firestore:', err);
      }
    }
    toast.success('Draft deleted successfully');
  };

  const clearAllDrafts = async () => {
    if (window.confirm("Are you sure you want to clear all drafts from your Pending Box? This will also delete them from the cloud.")) {
      try {
        localStorage.removeItem('pending_invoice_drafts');
        setPendingDrafts([]);
        setTimeout(() => {
          window.dispatchEvent(new Event('pending_drafts_updated'));
        }, 0);

        if (profile && navigator.onLine) {
          const q = query(
            collection(db, 'pending_invoice_drafts'),
            where('userId', '==', profile.uid)
          );
          const snapshot = await getDocs(q);
          for (const d of snapshot.docs) {
            await deleteDoc(d.ref);
          }
        }
        toast.success('All drafts cleared successfully');
      } catch (err) {
        console.error('Error clearing drafts:', err);
        toast.error('Failed to clear some cloud drafts');
      }
    }
  };

  const numberToWords = (num: number): string => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

    const convert_thousands = (n: number): string => {
      if (n >= 1000) return convert_thousands(Math.floor(n / 1000)) + " Thousand " + convert_hundreds(n % 1000);
      else return convert_hundreds(n);
    };

    const convert_hundreds = (n: number): string => {
      if (n > 99) return ones[Math.floor(n / 100)] + " Hundred " + convert_tens(n % 100);
      else return convert_tens(n);
    };

    const convert_tens = (n: number): string => {
      if (n < 10) return ones[n];
      else if (n >= 10 && n < 20) return teens[n - 10];
      else return tens[Math.floor(n / 10)] + " " + ones[n % 10];
    };

    if (num === 0) return "Zero";
    
    // Handle millions
    const convert_millions = (n: number): string => {
      if (n >= 1000000) return convert_millions(Math.floor(n / 1000000)) + " Million " + convert_thousands(n % 1000000);
      else return convert_thousands(n);
    };

    // Handle billions
    const convert_billions = (n: number): string => {
      if (n >= 1000000000) return convert_billions(Math.floor(n / 1000000000)) + " Billion " + convert_millions(n % 1000000000);
      else return convert_millions(n);
    };

    const mainPart = Math.floor(num);
    const decimalPart = Math.round((num - mainPart) * 100);

    let words = convert_billions(mainPart).trim();
    
    if (decimalPart > 0) {
      words += " And " + convert_tens(decimalPart).trim() + " Cents";
    } else {
      words += " And Cent Zero Zero";
    }

    return words;
  };

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'TZS' ? 'TZS' : currency,
      minimumFractionDigits: 0,
    }).format(amount).replace('TZS', 'TSh');
  };

  const generateSurveyReportPDF = (report: Report, returnBase64 = false) => {
    const doc = new jsPDF();
    
    // Header
    if (letterheadUrl) {
      try {
        doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
      } catch (e) {
        doc.setFontSize(20);
        doc.setTextColor(0, 51, 102);
        doc.text('TECHNICAL SURVEY REPORT', 105, 20, { align: 'center' });
      }
    } else {
      doc.setFontSize(20);
      doc.setTextColor(0, 51, 102);
      doc.text('TECHNICAL SURVEY REPORT', 105, 20, { align: 'center' });
    }
    
    const startY = letterheadUrl ? 50 : 30;

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 195, startY, { align: 'right' });
    
    // Client Info
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text('CLIENT INFORMATION', 20, startY + 10);
    doc.setFontSize(10);
    doc.text(`Client: ${report.clientName}`, 20, startY + 18);
    doc.text(`Branch: ${report.branchName || 'N/A'}`, 20, startY + 24);
    doc.text(`Technician: ${report.technicianName}`, 20, startY + 30);

    // Findings
    doc.setFontSize(12);
    doc.text('FINDINGS', 20, startY + 45);
    doc.setFontSize(10);
    const findingsLines = doc.splitTextToSize(report.findings || '', 170);
    doc.text(findingsLines, 20, startY + 53);

    // Recommendations
    const recommendationsY = startY + 53 + (findingsLines.length * 5) + 10;
    doc.setFontSize(12);
    doc.text('RECOMMENDATIONS', 20, recommendationsY);
    doc.setFontSize(10);
    const recLines = doc.splitTextToSize(report.recommendations || '', 170);
    doc.text(recLines, 20, recommendationsY + 8);

    // Spare Parts Table
    const tableY = recommendationsY + 8 + (recLines.length * 5) + 10;
    (doc as any).autoTable({
      startY: tableY,
      head: [['Spare Part', 'Quantity', 'Unit Price', 'Total']],
      body: [
        ...(report.requiredSpareParts || []).map(p => [
          p.name,
          p.quantity,
          formatCurrency(p.price || 0),
          formatCurrency(p.quantity * (p.price || 0))
        ]),
        ['Labour Charge', '-', '-', formatCurrency(report.labourCharge || 0)]
      ],
      foot: [['', '', 'TOTAL', formatCurrency((report.requiredSpareParts || []).reduce((sum, p) => sum + (p.quantity * (p.price || 0)), 0) + (report.labourCharge || 0))]],
      theme: 'grid',
      headStyles: { fillColor: [0, 51, 102] },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    if (returnBase64) {
      return doc.output('datauristring');
    }
    downloadFile(doc.output('blob'), `Survey_Report_${report.clientName}_${Date.now()}.pdf`);
  };

  const generateInvoicePDF = async (invoice: Invoice, outputFormat?: 'blob' | 'datauristring') => {
    if (!invoice || !invoice.items) {
      toast.error('Invoice data is incomplete');
      return;
    }

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const client = clients.find(c => c.id === invoice.clientId);
      const subtotal = invoice.items.reduce((sum, item) => {
        const amt = item.amount === '' ? 0 : Number(item.amount);
        return sum + (isNaN(amt) ? 0 : amt);
      }, 0);
      const currentVatRate = invoice.vatRate ?? 18;
      const currentVatAmount = calculateVatAmount(invoice.items, currentVatRate);
      const totalAmount = subtotal + currentVatAmount;
      const currency = invoice.currency || defaultCurrency;
      const bankAccount = bankAccounts.find(a => a.id === invoice.bankAccountId);

      // Automatically optimize layout spacing if there are many items to fit up to 17+ lines on a single A4 page
      const hasManyItems = invoice.items && invoice.items.length >= 8;
      
      const currentLayout = {
        ...layoutSettings,
        fontSize: invoice.fontSize || (hasManyItems ? Math.min(layoutSettings.fontSize, 8.5) : layoutSettings.fontSize),
        fontFamily: invoice.fontFamily || layoutSettings.fontFamily,
        fontStyle: invoice.fontStyle || layoutSettings.fontStyle,
        customElements: layoutSettings.customElements || []
      };

      // Set standard A4 boundaries
      currentLayout.marginTop = Math.max(currentLayout.marginTop, 10);
      currentLayout.marginLeft = Math.max(currentLayout.marginLeft, 10);
      currentLayout.marginRight = Math.max(currentLayout.marginRight, 10);
      
      const pageHeight = 297;
      const pageWidth = 210;
      const marginL = currentLayout.marginLeft;
      const marginR = currentLayout.marginRight;
      const contentWidth = pageWidth - marginL - marginR;

      // Position Document Title ABOVE the headed paper
      const docTitleY = currentLayout.marginTop + 4;
      // Headed paper/letterhead starts below the title
      const letterheadStartY = currentLayout.marginTop + 12;

      // Calculate where the header/letterhead ends to prevent overlap
      const logoW = Math.min(currentLayout.logoWidth || 25, 25);
      const letterheadHeightVal = letterheadUrl ? 35 : (logoUrl ? logoW + 4 : 24);
      const headerEndY = letterheadStartY + letterheadHeightVal;

      // Position Info Boxes (Client Address & Metadata) below the headed paper
      const infoBoxesY = headerEndY + 6;
      currentLayout.infoBoxesY = infoBoxesY;

      // Setup Info Rows to calculate height dynamically
      let dateStr = '...';
      try { if (invoice.createdAt) dateStr = formatLongDate(invoice.createdAt); } catch (e) {}
      let dueStr = '...';
      try { if (invoice.dueDate) dueStr = formatLongDate(invoice.dueDate); } catch (e) {}

      const infoRows = [
        { label: invoice.type === 'proforma' ? 'PFI No:' : 'Invoice No:', value: invoice.invoiceNumber || 'NEW' },
        { label: 'Date:', value: dateStr },
        { label: 'Valid Until:', value: dueStr }
      ];
      
      if (invoice.type === 'tax') {
        infoRows.push({ label: 'SMNO:', value: invoice.smNumber || '' });
        infoRows.push({ label: 'LPO NO:', value: invoice.lpoNumber || '' });
        infoRows.push({ label: 'D/NOTE:', value: invoice.deliveryNote || '' });
      } else {
        infoRows.push({ label: 'Sales Agent:', value: invoice.salesAgent || 'JACKSON MANDELA' });
        if (invoice.agentPhone) {
          infoRows.push({ label: 'Tel:', value: invoice.agentPhone });
        }
      }

      const rowHeight = 5.5; // compact row height to prevent vertical creep
      const totalInfoBoxHeight = Math.max(26, infoRows.length * rowHeight);
      const infoBoxesEndY = infoBoxesY + totalInfoBoxHeight;

      // Position Subject Box (Description Box) below the Info Boxes
      const descriptionStartY = infoBoxesEndY + 3;
      currentLayout.descriptionStartY = descriptionStartY;

      // Calculate Subject Box (Description Box) wrapped height dynamically
      const descText = invoice.description ? `RE: ${invoice.description}` : '';
      const wrapWidth = contentWidth - 16; // internal padding of 8mm on each side

      doc.setFont(currentLayout.fontFamily || 'helvetica', 'bold');
      doc.setFontSize(currentLayout.fontSize);
      const descLines = doc.splitTextToSize(descText, wrapWidth);
      const lineSpacing = 4.2;
      const boxPaddingY = 2.5;
      const dynamicBoxHeight = invoice.description ? (descLines.length * lineSpacing) + (boxPaddingY * 2) : 0;
      const descriptionEndY = descriptionStartY + dynamicBoxHeight;

      // Position Items Table below the Subject Box with a compact 1.5mm gap
      const itemsTableStartY = descriptionEndY + (invoice.description ? 1.5 : 0);

      // Footer totals and signatures section stay static at the bottom of the page
      const footerHeight = 52;
      const footerStartY = pageHeight - Math.max(currentLayout.marginBottom, 7) - footerHeight;
      currentLayout.footerY = footerStartY;

      const drawHeader = (data: any = {}) => {
        try {
          // Render Custom Elements first (background layers)
          currentLayout.customElements.forEach(el => {
            doc.setGState(new (doc as any).GState({ opacity: el.opacity ?? 1 }));
            
            if (el.type === 'rect' && el.width && el.height) {
              if (el.fill) {
                doc.setFillColor(el.fill);
                doc.rect(el.x, el.y, el.width, el.height, 'F');
              }
              if (el.stroke) {
                doc.setDrawColor(el.stroke);
                doc.setLineWidth(el.strokeWidth || 0.1);
                doc.rect(el.x, el.y, el.width, el.height, 'S');
              }
            } else if (el.type === 'line' && el.points) {
              doc.setDrawColor(el.stroke || '#000000');
              doc.setLineWidth(el.strokeWidth || 0.2);
              doc.line(el.x + el.points[0], el.y + el.points[1], el.x + el.points[2], el.y + el.points[3]);
            } else if (el.type === 'text' && el.text) {
              doc.setTextColor(el.fill || '#000000');
              doc.setFontSize(el.fontSize || 10);
              doc.setFont(currentLayout.fontFamily || 'helvetica', el.fontStyle || 'normal');
              doc.text(el.text, el.x, el.y + (el.fontSize || 10) / 3); // Basic vertical alignment adjustment
            } else if (el.type === 'image' && el.width && el.height) {
              // For designer images, we use a placeholder if no URL, but in real PDF we'd need a URL.
              // If it's the logo, handled separately, but custom images could be added.
              // For now, draw a subtle rectangle for custom images if no URL provided
              doc.setDrawColor(200, 200, 200);
              doc.setLineWidth(0.1);
              doc.rect(el.x, el.y, el.width, el.height, 'S');
              doc.setFontSize(6);
              doc.text('IMG AREA', el.x + 2, el.y + el.height/2);
            }
          });
          
          doc.setGState(new (doc as any).GState({ opacity: 1 }));
          doc.setTextColor(30, 41, 59);

          // Draw Custom Sections
          (currentLayout.sections || []).forEach(sec => {
            doc.setDrawColor(sec.style?.border ? '#e2e8f0' : '#ffffff');
            doc.setLineWidth(0.1);
            doc.setFillColor(sec.style?.backgroundColor || '#ffffff');
            doc.rect(sec.x, sec.y, sec.width, sec.height, sec.style?.border ? 'FD' : 'F');

            const cellW = sec.width / sec.cols;
            const cellH = sec.height / sec.rows;

            for (let r = 0; r < sec.rows; r++) {
               for (let c = 0; c < sec.cols; c++) {
                  const key = `${r}-${c}`;
                  const field = sec.fields?.[key];
                  if (field) {
                    let txt = '';
                    if (field === 'invoiceNumber') txt = invoice.invoiceNumber;
                    else if (field === 'createdAt') txt = formatLongDate(invoice.createdAt);
                    else if (field === 'dueDate') txt = formatLongDate(invoice.dueDate);
                    else if (field === 'clientName') txt = invoice.clientName;
                    else if (field === 'totalAmount') txt = formatCurrency(invoice.totalAmount, currency);
                    else if (field === 'notes') txt = invoice.notes || '';
                    else if (field === 'signatoryName') txt = invoice.signatoryName || '';
                    else if (field === 'signatoryTitle') txt = invoice.signatoryTitle || '';
                    else if (field === 'smNumber') txt = invoice.smNumber || '';
                    else if (field === 'lpoNumber') txt = invoice.lpoNumber || '';
                    else if (field === 'deliveryNote') txt = invoice.deliveryNote || '';

                    doc.setFontSize(sec.style?.fontSize || currentLayout.fontSize);
                    doc.setFont(currentLayout.fontFamily || 'helvetica', sec.style?.fontWeight || 'normal');
                    
                    const textX = sec.x + (c * cellW) + 2;
                    const textY = sec.y + (r * cellH) + (cellH / 2) + (sec.style?.fontSize || 10) / 4;
                    
                    doc.text(txt, textX, textY);
                  }
               }
            }
          });

          doc.setFontSize(currentLayout.fontSize + 4);
          doc.setFont(currentLayout.fontFamily || 'helvetica', 'bold');
          const docTitle = invoice.type === 'proforma' 
            ? 'PROFORMA INVOICE' 
            : (invoice.type === 'tax' ? 'TAX INVOICE' : 'INVOICE');
          doc.text(docTitle, 105, docTitleY, { align: 'center' });
          
          if (letterheadUrl) try { doc.addImage(letterheadUrl, 'PNG', 10, letterheadStartY, 190, 35); } catch (e) { console.error('Letterhead error:', e); }
          else {
            const logoW = Math.min(currentLayout.logoWidth || 25, 25);
            if (logoUrl) try { doc.addImage(logoUrl, 'PNG', currentLayout.marginLeft, letterheadStartY + 2, logoW, logoW); } catch (e) {}
            doc.setTextColor(220, 38, 38);
            doc.setFontSize(16);
            doc.setFont(currentLayout.fontFamily || 'helvetica', 'bold');
            doc.text('BRIGHT TECHNICAL SYSTEMS', 120, letterheadStartY + 8, { align: 'center' });
            doc.setFontSize(11);
            doc.text('& GENERAL SUPPLIES LIMITED', 120, letterheadStartY + 14, { align: 'center' });
          }

          doc.setTextColor(30, 41, 59);
          // Removed double lines below header as requested

          // Info Boxes
          const boxWidth = contentWidth / 2;
          doc.setDrawColor(0);
          doc.setLineWidth(0.1);

          // Left Box: Client Info
          doc.rect(currentLayout.marginLeft, infoBoxesY, boxWidth, totalInfoBoxHeight);
          doc.setFontSize(currentLayout.fontSize);
          doc.setFont(currentLayout.fontFamily || 'helvetica', 'bold');
          doc.text(invoice.clientName || 'N/A', currentLayout.marginLeft + 5, infoBoxesY + 5.5);
          doc.setFont(currentLayout.fontFamily || 'helvetica', currentLayout.fontStyle || 'normal');
          
          const clientAddress = client?.address || '';
          if (clientAddress) {
            const addrLines = doc.splitTextToSize(clientAddress, boxWidth - 10);
            doc.text(addrLines, currentLayout.marginLeft + 5, infoBoxesY + 11.5);
          }
          
          // Right Box: Info Table with Grid (Borders and Columns)
          const infoBoxX = currentLayout.marginLeft + boxWidth;
          const col1Width = 35; // Title column
          const col2Width = boxWidth - col1Width; // Value column

          infoRows.forEach((row, idx) => {
            const y = infoBoxesY + (idx * rowHeight);
            
            // Draw cells (Borders)
            doc.setDrawColor(0);
            doc.setLineWidth(0.1);
            doc.rect(infoBoxX, y, col1Width, rowHeight);
            doc.rect(infoBoxX + col1Width, y, col2Width, rowHeight);
            
            // Label (Left Column)
            doc.setFontSize(currentLayout.fontSize);
            doc.setFont(currentLayout.fontFamily || 'helvetica', 'bold');
            doc.text(row.label, infoBoxX + 2, y + 4);
            
            // Value (Right Column)
            doc.setFont(currentLayout.fontFamily || 'helvetica', currentLayout.fontStyle || 'normal');
            doc.text(String(row.value), infoBoxX + col1Width + 2, y + 4);
          });

          // Fill extra space if any up to totalInfoBoxHeight
          if (infoRows.length * rowHeight < totalInfoBoxHeight) {
            const remainingY = infoBoxesY + (infoRows.length * rowHeight);
            const remainingH = totalInfoBoxHeight - (infoRows.length * rowHeight);
            doc.rect(infoBoxX, remainingY, col1Width, remainingH);
            doc.rect(infoBoxX + col1Width, remainingY, col2Width, remainingH);
          }

          // Description Box - Full width box, height auto adjusts up and down according to text entered
          if (invoice.description) {
            const descText = `RE: ${invoice.description}`;
            
            doc.setFont(currentLayout.fontFamily || 'helvetica', 'bold');
            doc.setFontSize(currentLayout.fontSize);
            
            const descLines = doc.splitTextToSize(descText, wrapWidth);
            const lineSpacing = 4.2;
            const boxPaddingY = 2.5; // dynamic padding Y
            
            const boxHeight = (descLines.length * lineSpacing) + (boxPaddingY * 2);
            const startY = descriptionStartY;

            // Draw box spanning full content width (left and right static)
            doc.setDrawColor(0);
            doc.setLineWidth(0.1);
            doc.setFillColor(248, 250, 252);
            doc.rect(marginL, startY, contentWidth, boxHeight, 'FD');
            
            // Center of the content area for aligning centered text
            const centerX = marginL + (contentWidth / 2);
            
            doc.setTextColor(0);
            const fontSizeMm = (currentLayout.fontSize || 9.5) * 0.352778; // convert pt to mm
            descLines.forEach((line: string, index: number) => {
              // Perfectly center the text line(s) vertically in the dynamic box
              const lineY = startY + boxPaddingY + (index * lineSpacing) + (lineSpacing + fontSizeMm) / 2;
              doc.text(line, centerX, lineY, { align: 'center' });
            });
          }
        } catch (err) {
          console.error('Inner PDF render error:', err);
        }
      };

      // 6. Items Table Calculation
    const tableBody = invoice.items.map((item) => {
      const isBlank = !item.description && !item.quantity && !item.unitPrice;
      if (isBlank) {
        return ['', '', '', '', '', '', ''];
      }
      return [
        item.serialNumber || '',
        item.description || '',
        item.quantity || '',
        item.unit || '',
        item.unitPrice !== '' ? Number(item.unitPrice).toLocaleString() : '',
        item.amount !== '' ? Number(item.amount).toLocaleString() : ''
      ];
    });

    const currentDescText = invoice.description ? `RE: ${invoice.description}` : '';

    autoTable(doc, {
      startY: itemsTableStartY,
      head: [['S/N', 'Description', 'Qty', 'Unit', 'Rate', 'Amount']],
      body: tableBody,
      theme: 'plain',
      showHead: 'everyPage',
      headStyles: { 
        fillColor: [241, 245, 249], 
        textColor: [0, 0, 0], 
        fontStyle: 'bold', 
        lineWidth: 0.1, 
        lineColor: [0,0,0] 
      },
      styles: { 
        fontSize: currentLayout.fontSize, 
        cellPadding: 1, 
        minCellHeight: currentLayout.itemsRowHeight,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        font: currentLayout.fontFamily || 'helvetica'
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        2: { cellWidth: 15, halign: 'center' },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 25, halign: 'right' }
      },
      margin: { 
        top: itemsTableStartY, 
        bottom: pageHeight - footerStartY + 5, 
        left: currentLayout.marginLeft, 
        right: currentLayout.marginRight 
      },
      didDrawPage: drawHeader, 
      didDrawCell: (data) => {
        if (data.section === 'body' || data.section === 'head') {
          doc.setDrawColor(0);
          doc.setLineWidth(0.1);
          doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
          if (data.column.index === data.table.columns.length - 1) {
            doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
          }
          if (data.section === 'head') {
             doc.line(data.cell.x, data.cell.y, data.cell.x + data.cell.width, data.cell.y);
             doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
          }
        }
      }
    });

    const drawPageFooterExtras = (pageNum: number, totalPages: number) => {
      doc.setPage(pageNum);
      const isLastPage = pageNum === totalPages;
      const bankDetails = (bankAccount && invoice.type !== 'proforma') 
        ? `Bank: ${bankAccount.name}\nBranch Name: ${bankAccount.branchName || ''}\nBranch Code: ${bankAccount.branchCode || ''}\nAccount: ${bankAccount.accountNumber || bankAccount.id}\nAccount Name: ${bankAccount.accountName || 'Bright Technical Systems'}` 
        : '';

      const localSubtotal = isLastPage ? `${currency} ${subtotal.toLocaleString()}` : '';
      const localVat = isLastPage ? `${currency} ${currentVatAmount.toLocaleString()}` : '';
      const localTotal = isLastPage ? `${currency} ${totalAmount.toLocaleString()}` : '';
      const localWords = isLastPage ? `${numberToWords(totalAmount)} ${currency.charAt(0) + currency.slice(1).toLowerCase()} Only.` : '';
      const boxW = 210 - currentLayout.marginLeft - currentLayout.marginRight;

      autoTable(doc, {
        startY: footerStartY,
        body: [
          [
            { content: 'E & O.E', styles: { halign: 'center', valign: 'middle', fontStyle: 'bold', font: currentLayout.fontFamily || 'helvetica' } },
            { content: 'Subtotal', styles: { font: currentLayout.fontFamily || 'helvetica' } },
            { content: localSubtotal, styles: { halign: 'right', font: currentLayout.fontFamily || 'helvetica' } }
          ],
          [
            { 
              content: bankDetails, 
              rowSpan: 2, 
              styles: { 
                fontSize: 7, 
                valign: 'top', 
                font: currentLayout.fontFamily || 'helvetica', 
                textColor: invoice.type === 'tax' ? [0, 0, 255] : [0, 0, 0] // Blue for Tax Invoices
              } 
            },
            { content: `VAT ${currentVatRate}%`, styles: { font: currentLayout.fontFamily || 'helvetica' } },
            { content: localVat, styles: { halign: 'right', font: currentLayout.fontFamily || 'helvetica' } }
          ],
          [
            { content: 'Total', styles: { fontStyle: 'bold', fontSize: 10, font: currentLayout.fontFamily || 'helvetica' } },
            { content: localTotal, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, font: currentLayout.fontFamily || 'helvetica' } }
          ]
        ],
        theme: 'grid',
        styles: { fontSize: currentLayout.fontSize, cellPadding: 1, minCellHeight: 3.97, lineColor: [0, 0, 0], lineWidth: 0.1, font: currentLayout.fontFamily || 'helvetica' },
        columnStyles: { 0: { cellWidth: boxW * 0.5 }, 1: { cellWidth: boxW * 0.25 }, 2: { cellWidth: boxW * 0.25 } },
        margin: { left: currentLayout.marginLeft, right: currentLayout.marginRight, top: 0, bottom: 0 }
      });

      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY,
        body: [
          [
            { content: 'AMOUNT IN WORDS', styles: { fontStyle: 'bold', cellWidth: 35, fontSize: 7.5, font: currentLayout.fontFamily || 'helvetica' } },
            { content: localWords, styles: { fontStyle: 'normal', font: currentLayout.fontFamily || 'helvetica' } }
          ]
        ],
        theme: 'grid',
        styles: { fontSize: currentLayout.fontSize, cellPadding: 1.5, minCellHeight: 3.97, lineColor: [0, 0, 0], lineWidth: 0.1, font: currentLayout.fontFamily || 'helvetica' },
        margin: { left: currentLayout.marginLeft, right: currentLayout.marginRight, top: 0, bottom: 0 }
      });

      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY,
        body: [
          [
            { 
              // Added space for signature above name and dotted line
              content: `\n\n..................................................\n${invoice.signatoryName || ' '}\n${invoice.signatoryTitle || ' '}`,
              styles: { minCellHeight: currentLayout.signatureHeight, valign: 'bottom', font: currentLayout.fontFamily || 'helvetica' }
            }
          ]
        ],
        theme: 'grid',
        styles: { fontSize: currentLayout.fontSize, cellPadding: 2, fontStyle: 'bold', lineColor: [0, 0, 0], lineWidth: 0.1, font: currentLayout.fontFamily || 'helvetica' },
        margin: { left: currentLayout.marginLeft, right: currentLayout.marginRight, top: 0, bottom: 0 }
      });

      if (isLastPage || invoice.type === 'proforma') {
        const currentSignatureUrl = invoice.signatureUrl || signatureUrl; 
        if (currentSignatureUrl && invoice.type !== 'tax') {
          const tableBottomY = (doc as any).lastAutoTable.finalY;
          // Positioned signature image directly above the name and dotted line
          const sigY = tableBottomY - 26; 
          try {
            doc.addImage(currentSignatureUrl, 'PNG', currentLayout.marginLeft + 10, sigY, 35, 12);
          } catch (e) {}
        }
      }
    };

    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      drawPageFooterExtras(i, totalPages);
    }

    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(0);
        doc.setLineWidth(0.1);
        
        const bW = (210 - currentLayout.marginLeft - currentLayout.marginRight) / 2;
        
        // Only draw header boxes on the first page if you want them only there, 
        // but since didDrawPage calls drawHeader which draws them, we should be consistent.
        // The drawHeader is called on EVERY page currently.
        
        doc.rect(currentLayout.marginLeft, currentLayout.infoBoxesY, bW, totalInfoBoxHeight);
        doc.rect(currentLayout.marginLeft + bW, currentLayout.infoBoxesY, bW, totalInfoBoxHeight);
        
        const lineStartY = itemsTableStartY; // Consistent top margin for all pages
        const lineEndY = footerStartY;
        
        const snW = 10;
        const qtyW = 15;
        const unitW = 15;
        const rateW = 25;
        const amountW = 25;
        const tableTotalW = 210 - currentLayout.marginLeft - currentLayout.marginRight;
        const descW = tableTotalW - (snW + qtyW + unitW + rateW + amountW);

        const colX = [
          currentLayout.marginLeft, // Left edge
          currentLayout.marginLeft + snW, // S/N | Description
          currentLayout.marginLeft + snW + descW, // Description | Qty
          currentLayout.marginLeft + snW + descW + qtyW, // Qty | Unit
          currentLayout.marginLeft + snW + descW + qtyW + unitW, // Unit | Rate
          currentLayout.marginLeft + snW + descW + qtyW + unitW + rateW, // Rate | Amount
          currentLayout.marginLeft + snW + descW + qtyW + unitW + rateW + amountW // Right edge
        ];
        colX.forEach(x => {
          doc.line(x, lineStartY, x, lineEndY);
        });

        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} of ${totalPages}`, 105, pageHeight - 3, { align: 'center' });
    }

    if (outputFormat === 'blob') {
      return doc.output('blob');
    }
    if (outputFormat === 'datauristring') {
      return doc.output('datauristring');
    }

    const fileName = `${invoice.type === 'proforma' ? 'Proforma' : (invoice.type === 'tax' ? 'Tax' : 'Invoice')}_${invoice.invoiceNumber}.pdf`;

    downloadFile(doc.output('blob'), fileName).then(success => {
      if (success) toast.success('Invoice generated successfully');
    });
    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.error('Failed to generate PDF. Please check invoice details.');
    }
  };

  const exportToCSV = () => {
    if (filteredInvoices.length === 0) {
      toast.error('No invoices to export');
      return;
    }

    const headers = [
      'Invoice Number',
      'Client Name',
      'Total Amount',
      'Status',
      'Due Date',
      'Created At'
    ];

    const csvRows = filteredInvoices.map(invoice => [
      invoice.invoiceNumber,
      invoice.clientName,
      invoice.totalAmount.toString(),
      invoice.status.toUpperCase(),
      safeToDate(invoice.dueDate).toLocaleDateString(),
      safeToDate(invoice.createdAt).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(value => `"${value.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, `invoices_export_${new Date().toISOString().split('T')[0]}.csv`).then(success => {
      if (success) toast.success('Invoices exported successfully');
    });
  };

  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-600',
      sent: 'bg-blue-100 text-blue-600',
      paid: 'bg-emerald-100 text-emerald-600',
      overdue: 'bg-red-100 text-red-600',
      cancelled: 'bg-slate-200 text-slate-400'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const getTypeBadge = (type: Invoice['type']) => {
    const styles: Record<string, string> = {
      formal: 'bg-blue-900 text-white',
      proforma: 'bg-orange-500 text-white',
      tax: 'bg-emerald-600 text-white'
    };
    const safeType = type || 'formal';
    return (
      <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${styles[safeType] || styles.formal}`}>
        {safeType}
      </span>
    );
  };

  const handleBatchStatusUpdate = async (status: Invoice['status']) => {
    if (selectedInvoiceIds.length === 0) return;
    setConfirmModal({
      isOpen: true,
      title: 'Batch Update Status',
      message: `Are you sure you want to mark ${selectedInvoiceIds.length} invoices as ${status}?`,
      onConfirm: async () => {
        setIsBatchProcessing(true);
        try {
          // Adjust account balances first for each invoice
          for (const id of selectedInvoiceIds) {
            const invoice = invoices.find(inv => inv.id === id);
            if (invoice) {
              await adjustAccountBalance(
                invoice.status,
                status,
                invoice.bankAccountId,
                invoice.bankAccountId,
                invoice.totalAmount,
                invoice.totalAmount
              );
            }
          }

          const promises = selectedInvoiceIds.map(id => 
            updateDoc(doc(db, 'invoices', id), { 
              status, 
              updatedAt: Timestamp.now() 
            })
          );
          await Promise.all(promises);
          toast.success(`Successfully updated ${selectedInvoiceIds.length} invoices`);
          setSelectedInvoiceIds([]);
        } catch (error) {
          console.error('Batch update error:', error);
          toast.error('Failed to update some invoices');
        } finally {
          setIsBatchProcessing(false);
        }
      }
    });
  };

  const handleSingleMarkAsPaid = async (invoice: Invoice) => {
    if (!invoice?.id) return;
    
    // If invoice does not have a bankAccountId set, we should ask them to select one
    if (!invoice.bankAccountId) {
      setBankAccountSelectInvoice(invoice);
      setSelectedBankAccountId('');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Mark Invoice as Paid',
      message: `Are you sure you want to mark Invoice #${invoice.invoiceNumber} as Paid? This will add the billed amount (${invoice.currency || defaultCurrency} ${invoice.totalAmount.toLocaleString()}) to the selected bank account.`,
      confirmLabel: 'Yes, Mark Paid',
      cancelLabel: 'Cancel',
      isDanger: false,
      onConfirm: async () => {
        try {
          // Adjust account balance
          await adjustAccountBalance(
            invoice.status,
            'paid',
            invoice.bankAccountId,
            invoice.bankAccountId,
            invoice.totalAmount,
            invoice.totalAmount
          );

          // Update invoice status
          await updateDoc(doc(db, 'invoices', invoice.id!), {
            status: 'paid',
            updatedAt: Timestamp.now()
          });

          toast.success(`Invoice #${invoice.invoiceNumber} marked as Paid successfully`);
          if (viewingInvoice?.id === invoice.id) {
            setViewingInvoice(prev => prev ? { ...prev, status: 'paid' } : null);
          }
        } catch (error) {
          console.error('Error marking invoice as paid:', error);
          toast.error('Failed to mark invoice as paid');
        }
      }
    });
  };

  const handleSaveBankAndMarkPaid = async () => {
    if (!bankAccountSelectInvoice || !selectedBankAccountId) return;
    const invoice = bankAccountSelectInvoice;

    try {
      // Adjust account balance
      await adjustAccountBalance(
        invoice.status,
        'paid',
        null,
        selectedBankAccountId,
        0,
        invoice.totalAmount
      );

      // Update invoice bankAccountId and status
      await updateDoc(doc(db, 'invoices', invoice.id!), {
        bankAccountId: selectedBankAccountId,
        status: 'paid',
        updatedAt: Timestamp.now()
      });

      toast.success(`Invoice #${invoice.invoiceNumber} marked as Paid and updated with bank account`);
      if (viewingInvoice?.id === invoice.id) {
        setViewingInvoice(prev => prev ? { ...prev, bankAccountId: selectedBankAccountId, status: 'paid' } : null);
      }
      setBankAccountSelectInvoice(null);
    } catch (error) {
      console.error('Error saving bank and marking paid:', error);
      toast.error('Failed to update invoice');
    }
  };

  const handleBatchEmailSend = async () => {
    if (selectedInvoiceIds.length === 0) return;
    setConfirmModal({
      isOpen: true,
      title: 'Batch Email Send',
      message: `Are you sure you want to send ${selectedInvoiceIds.length} invoices via email?`,
      onConfirm: async () => {
        setIsBatchProcessing(true);
        let successCount = 0;
        let failCount = 0;

        try {
          for (const id of selectedInvoiceIds) {
            const invoice = invoices.find(inv => inv.id === id);
            if (!invoice) continue;

            const client = clients.find(c => c.id === invoice.clientId);
            if (!client?.email) {
              failCount++;
              continue;
            }

            const pdfDataUri = await generateInvoicePDF(invoice, 'datauristring') as string;
            const pdfBase64 = pdfDataUri.split(',')[1];
            const itemsList = invoice.items.map(item => `- ${item.description}: ${invoice.currency || defaultCurrency} ${item.amount.toLocaleString()}`).join('\n');
        const body = `Dear ${client.name},\n\nPlease find attached your ${invoice.type === 'proforma' ? 'proforma invoice' : 'invoice'} ${invoice.invoiceNumber}.\n\nInvoice Details:\n${itemsList}\n\nTotal Amount: ${invoice.currency || defaultCurrency} ${invoice.totalAmount.toLocaleString()}\n\nBest regards,\nBright Technical Systems`;

        try {
          const response = await fetch('/api/send-invoice', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: client.email,
              subject: `${invoice.type === 'proforma' ? 'Proforma Invoice' : 'Invoice'} ${invoice.invoiceNumber} - Bright Technical Systems`,
              body,
              pdfBase64,
              fileName: `${invoice.type === 'proforma' ? 'Proforma' : 'Invoice'}_${invoice.invoiceNumber}.pdf`
            }),
          });

          if (response.ok) {
            await updateDoc(doc(db, 'invoices', id), { status: 'sent', updatedAt: Timestamp.now() });
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }

      toast.success(`Sent ${successCount} emails successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      setSelectedInvoiceIds([]);
    } catch (error) {
      console.error('Batch email error:', error);
      toast.error('An error occurred during batch email sending');
    } finally {
      setIsBatchProcessing(false);
    }
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedInvoiceIds.length === filteredInvoices.length) {
      setSelectedInvoiceIds([]);
    } else {
      setSelectedInvoiceIds(filteredInvoices.map(inv => inv.id!));
    }
  };

  const toggleSelectInvoice = (id: string) => {
    setSelectedInvoiceIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleConvertToTaxInvoice = async (invoice: Invoice) => {
    if (!isAdmin && !isManager && !isBilling && !isAccountant && !isCashier) return;
    if (!invoice?.id) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Convert to Tax Invoice',
      message: 'Are you sure you want to convert this Proforma Invoice to a Tax Invoice? This will create a NEW Tax Invoice and keep the original Proforma.',
      confirmLabel: 'Yes, Convert',
      cancelLabel: 'No, Cancel',
      isDanger: false,
      onConfirm: async () => {
        // We keep 'tax' as the primary target for conversion
        const newType = 'tax';
        const newInvoiceNumber = getNextInvoiceNumber();
        const d = new Date();
        d.setMonth(d.getMonth() + 1);
        const newDueDate = d.toISOString().split('T')[0];

        // Set converting state so handleSubmit knows to link them
        setConvertingFromProformaId(invoice.id);
        
        // Set editing invoice to null to create a NEW record
        setEditingInvoice(null);

        // Prepare form data with existing proforma info but updated for Tax Invoice
        setFormData({
          invoiceNumber: newInvoiceNumber,
          clientId: invoice.clientId,
          branchId: invoice.branchId || '',
          reportId: invoice.reportId || '',
          serviceType: invoice.serviceType || '',
          items: invoice.items.map(item => ({ ...item, vatStatus: item.vatStatus || 'VAT' })),
          description: invoice.description || '',
          notes: invoice.notes || '',
          type: newType,
          template: invoice.template || 'standard',
          bankAccountId: invoice.bankAccountId || '',
          signatoryName: invoice.signatoryName || profile?.displayName || '',
          signatoryTitle: invoice.signatoryTitle || profile?.role || '',
          signatureUrl: invoice.signatureUrl || signatureUrl || '',
          // User requested Sales Agent/Tel to be removed - we clear them here so they aren't saved/shown in the tax invoice
          salesAgent: '',
          agentPhone: '',
          smNumber: invoice.smNumber || '',
          lpoNumber: invoice.lpoNumber || '',
          deliveryNote: invoice.deliveryNote || '',
          status: invoice.status,
          dueDate: newDueDate,
          fontSize: invoice.fontSize || 11,
          fontFamily: invoice.fontFamily || 'helvetica',
          fontStyle: invoice.fontStyle || 'normal'
        });

        setIsModalOpen(true);
      }
    });
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    
    // Filter by Tab (Type)
    const matchesTab = activeTab === 'tax' ? (invoice.type === 'tax' || invoice.type === 'formal') : (invoice.type === 'proforma');
    
    // Filter by sales agent: Check creatorId or salesAgent name
    const matchesAgent = selectedAgentIds.length === 0 || 
                         (invoice.creatorId && selectedAgentIds.includes(invoice.creatorId)) ||
                         (invoice.salesAgent && billingUsers.some(u => selectedAgentIds.includes(u.uid) && u.displayName === invoice.salesAgent));
    
    return matchesSearch && matchesStatus && matchesAgent && matchesTab;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-blue-800" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Receipt className="w-10 h-10 text-blue-800" />
            Invoices
          </h1>
          <p className="text-slate-500 mt-1 font-medium italic">Manage billing and client payments</p>
        </div>
        <div className="flex items-center gap-3">
          {showList && (
            <button
              onClick={() => setShowList(false)}
              className="flex items-center gap-2 bg-slate-100 text-slate-600 px-6 py-3 rounded-2xl font-bold hover:bg-slate-200 transition-all shadow-sm cursor-pointer"
            >
              <Search className="w-5 h-5" />
              Search View
            </button>
          )}
          <button
            onClick={exportToCSV}
            className="flex items-center gap-2 bg-white text-slate-700 border border-slate-200 px-6 py-3 rounded-2xl font-bold hover:bg-slate-50 transition-all shadow-sm cursor-pointer"
          >
            <FileDown className="w-5 h-5 text-blue-800" />
            Export CSV
          </button>
          <button
            onClick={() => {
              const tid = toast.loading('Re-syncing invoice records with Firestore...');
              setTimeout(() => {
                toast.dismiss(tid);
                toast.success('Invoices successfully re-synchronized with cloud database.');
              }, 600);
            }}
            className="flex items-center gap-2 bg-white text-slate-700 border border-slate-200 px-5 py-3 rounded-2xl font-bold hover:bg-slate-50 transition-all shadow-sm cursor-pointer"
            title="Re-sync data with cloud"
          >
            <RefreshCw className="w-4 h-4 text-blue-600" />
            Re-Sync
          </button>
          {(isAdmin || isManager || isBilling) && (
            <>
              <button
                onClick={() => navigate('/invoice-designer')}
                className="flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 px-6 py-3 rounded-2xl font-bold hover:bg-emerald-100 transition-all shadow-sm cursor-pointer"
              >
                <Layout className="w-5 h-5 text-emerald-600" />
                Design Template
              </button>
              <button
                onClick={() => setIsPendingBoxOpen(true)}
                className="flex items-center gap-2 bg-slate-100 text-slate-700 border border-slate-200 px-6 py-3 rounded-2xl font-bold hover:bg-slate-200 transition-all shadow-sm cursor-pointer relative"
              >
                <Inbox className="w-5 h-5 text-orange-500" />
                <span>Pending Box</span>
                {pendingDrafts.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-orange-500 text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                    {pendingDrafts.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => openModal()}
                className="flex items-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 cursor-pointer"
              >
                <Plus className="w-5 h-5" />
                New Invoice
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-slate-100/50 p-1.5 rounded-2xl w-fit border border-slate-200/50">
        <button
          onClick={() => setActiveTab('tax')}
          className={cn(
            "px-8 py-3 rounded-xl font-black transition-all text-sm flex items-center gap-2 cursor-pointer",
            activeTab === 'tax' 
              ? "bg-white text-blue-800 shadow-lg shadow-blue-900/5 border border-slate-100" 
              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
          )}
        >
          <Receipt className="w-4 h-4" />
          Tax Invoices
        </button>
        <button
          onClick={() => setActiveTab('proforma')}
          className={cn(
            "px-8 py-3 rounded-xl font-black transition-all text-sm flex items-center gap-2 cursor-pointer",
            activeTab === 'proforma' 
              ? "bg-white text-blue-800 shadow-lg shadow-blue-900/5 border border-slate-100" 
              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
          )}
        >
          <FileText className="w-4 h-4" />
          Proforma Invoices
        </button>
      </div>

      {!showList ? (
        <div className="py-20 animate-in fade-in zoom-in duration-500">
          <div className="bg-white p-12 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-blue-900/5 text-center space-y-8 flex flex-col items-center max-w-2xl mx-auto">
            <div className="w-24 h-24 bg-blue-50 rounded-3xl flex items-center justify-center text-blue-800 relative">
              <Receipt className="w-12 h-12" />
              <div className="absolute -top-2 -right-2 bg-blue-800 text-white w-8 h-8 rounded-full flex items-center justify-center border-4 border-white">
                <Search className="w-4 h-4" />
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">Invoice Repository</h2>
              <p className="text-slate-500 font-medium max-w-sm mx-auto">Enter an invoice number or client name to search, or browse your full collection.</p>
            </div>
            <div className="w-full relative group">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400 group-focus-within:text-blue-800 transition-colors" />
              <input
                type="text"
                placeholder="Enter Client Name or Invoice Number..."
                className="w-full pl-16 pr-6 py-6 bg-slate-50 border-2 border-transparent focus:border-blue-800 focus:bg-white transition-all rounded-[2rem] font-bold text-lg shadow-inner"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { 
                  if(e.key === 'Enter' && searchTerm.trim()) {
                    setShowList(true);
                  }
                }}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
              <button 
                onClick={() => {
                  if(searchTerm.trim()) setShowList(true);
                  else toast.error('Please enter a search term or click View All');
                }}
                className="flex-1 sm:flex-none px-10 py-5 bg-blue-800 text-white rounded-[2rem] font-black uppercase tracking-widest hover:bg-blue-900 transition-all shadow-xl shadow-blue-800/20 active:scale-95"
              >
                Search Invoices
              </button>
              <button 
                onClick={() => setShowList(true)}
                className="flex-1 sm:flex-none px-10 py-5 bg-slate-100 text-slate-700 rounded-[2rem] font-black uppercase tracking-widest hover:bg-slate-200 transition-all active:scale-95"
              >
                View All Invoices
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
              <Receipt className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Total Invoices</p>
              <p className="text-2xl font-black text-slate-900">{invoices.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Paid</p>
              <p className="text-2xl font-black text-slate-900">
                {invoices.filter(i => i.status === 'paid').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Pending</p>
              <p className="text-2xl font-black text-slate-900">
                {invoices.filter(i => i.status === 'sent').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center text-red-600">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Overdue</p>
              <p className="text-2xl font-black text-slate-900">
                {invoices.filter(i => i.status === 'overdue').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 space-y-2">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by invoice number or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-medium"
            />
          </div>
          <button
            onClick={() => setIsAgentFilterOpen(true)}
            className="text-[10px] font-black text-blue-800 uppercase tracking-widest hover:text-blue-900 flex items-center gap-1.5 px-1 cursor-pointer"
          >
            <UserCheck className="w-3 h-3" />
            Search by Sales Agent
            {selectedAgentIds.length > 0 && (
              <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full ml-1">
                {selectedAgentIds.length}
              </span>
            )}
          </button>
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all text-sm font-semibold text-slate-600 self-start h-[50px]"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Batch Actions Bar */}
      <AnimatePresence>
        {selectedInvoiceIds.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[50] bg-blue-900 text-white px-8 py-4 rounded-3xl shadow-2xl flex items-center gap-8 border border-white/10 backdrop-blur-md"
          >
            <div className="flex items-center gap-3 border-r border-white/20 pr-8">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center font-black">
                {selectedInvoiceIds.length}
              </div>
              <span className="font-bold text-sm">Selected</span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBatchStatusUpdate('paid')}
                disabled={isBatchProcessing}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-xl transition-all text-sm font-bold disabled:opacity-50 cursor-pointer"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Mark Paid
              </button>
              <button
                onClick={() => handleBatchStatusUpdate('cancelled')}
                disabled={isBatchProcessing}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-xl transition-all text-sm font-bold disabled:opacity-50 cursor-pointer"
              >
                <X className="w-4 h-4 text-red-400" />
                Cancel
              </button>
              <button
                onClick={handleBatchEmailSend}
                disabled={isBatchProcessing}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-xl transition-all text-sm font-bold disabled:opacity-50 cursor-pointer"
              >
                <Mail className="w-4 h-4 text-blue-400" />
                Send Email
              </button>
            </div>

            <button
              onClick={() => setSelectedInvoiceIds([])}
              className="p-2 hover:bg-white/10 rounded-xl transition-all"
            >
              <X className="w-5 h-5" />
            </button>

            {isBatchProcessing && (
              <div className="absolute inset-0 bg-blue-900/80 rounded-3xl flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invoices List */}
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-6 py-5 w-10">
                  <input
                    type="checkbox"
                    checked={selectedInvoiceIds.length === filteredInvoices.length && filteredInvoices.length > 0}
                    onChange={toggleSelectAll}
                    className="w-5 h-5 rounded border-slate-300 text-blue-800 focus:ring-blue-800/20 cursor-pointer"
                  />
                </th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">Invoice</th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">Type</th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">Client</th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">Amount</th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">Prepared By</th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">Due Date</th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredInvoices.map((invoice, index) => (
                <motion.tr 
                  key={`inv-${invoice.id}-${index}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={cn(
                    "group transition-colors",
                    selectedInvoiceIds.includes(invoice.id!) ? "bg-blue-50/50" : "hover:bg-slate-50/50"
                  )}
                >
                  <td className="px-6 py-5">
                    <input
                      type="checkbox"
                      checked={selectedInvoiceIds.includes(invoice.id!)}
                      onChange={() => toggleSelectInvoice(invoice.id!)}
                      className="w-5 h-5 rounded border-slate-300 text-blue-800 focus:ring-blue-800/20 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 flex items-center gap-2">
                          #{invoice.invoiceNumber}
                          {invoice.billedUnderInvoiceNo && (
                            <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-1 rounded-lg font-bold border border-emerald-200 flex items-center gap-1 mt-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Billed under Tax Invoice #{invoice.billedUnderInvoiceNo}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400 font-medium">{safeToDate(invoice.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    {getTypeBadge(invoice.type)}
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <p className="font-bold text-slate-700">{invoice.clientName}</p>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <p className="font-black text-slate-900">{invoice.currency || defaultCurrency} {invoice.totalAmount.toLocaleString()}</p>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2">
                       <UserCheck className="w-3.5 h-3.5 text-slate-400" />
                       <span className="text-sm font-semibold text-slate-600">
                         {invoice.creatorName || invoice.salesAgent || (invoice.creatorId ? usersMap[invoice.creatorId] : null) || invoice.creatorEmail || 'Unknown'}
                       </span>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <p className="text-sm font-bold text-slate-600">{safeToDate(invoice.dueDate).toLocaleDateString()}</p>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={async () => {
                            const blob = await generateInvoicePDF(invoice, 'blob') as unknown as Blob;
                            const url = URL.createObjectURL(blob);
                            setPreviewUrl(url);
                            setViewingInvoice(invoice);
                            setIsPreviewModalOpen(true);
                        }}
                        className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                        title="Preview PDF"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => {
                          setViewingInvoice(invoice);
                          setIsViewModalOpen(true);
                        }}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                        title="System View"
                      >
                        <FileText className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => { generateInvoicePDF(invoice); }}
                        className="p-2 text-slate-400 hover:text-orange-500 hover:bg-orange-50 rounded-xl transition-all"
                        title="Download PDF"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          const client = clients.find(c => c.id === invoice.clientId);
                          const itemsList = invoice.items.map(item => `- ${item.description}: ${invoice.currency || defaultCurrency} ${item.amount.toLocaleString()}`).join('\n');
                          const body = `Dear ${client?.name || 'Client'},\n\nPlease find attached your ${invoice.type === 'proforma' ? 'proforma invoice' : 'invoice'} ${invoice.invoiceNumber}.\n\nInvoice Details:\n${itemsList}\n\nTotal Amount: ${invoice.currency || defaultCurrency} ${invoice.totalAmount.toLocaleString()}\n\nBest regards,\nBright Technical Systems`;
                          
                          setEmailData({
                            to: client?.email || '',
                            cc: '',
                            subject: `${invoice.type === 'proforma' ? 'Proforma Invoice' : 'Invoice'} ${invoice.invoiceNumber} - Bright Technical Systems`,
                            body: body,
                            invoiceId: invoice.id!
                          });
                          setIsEmailModalOpen(true);
                        }}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                        title="Send via Email"
                      >
                        <Mail className="w-5 h-5" />
                      </button>
                      {(invoice.type === 'proforma') && (isAdmin || isManager || isBilling || isAccountant || isCashier) && (
                        <button 
                          onClick={() => handleConvertToTaxInvoice(invoice)}
                          className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                          title="Convert to Tax Invoice"
                        >
                          <Receipt className="w-5 h-5" />
                        </button>
                      )}
                      {(isAdmin || isManager || isBilling || isAccountant || isCashier) && (
                        <>
                          <button 
                            onClick={() => handleDuplicateInvoice(invoice)}
                            className="p-2 text-slate-400 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all"
                            title="Duplicate Invoice"
                          >
                            <PlusCircle className="w-5 h-5" />
                          </button>
                          {(invoice.type === 'tax' || invoice.type === 'formal') && (
                            <button 
                              onClick={() => handleRestoreAsProforma(invoice)}
                              className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
                              title="Restore as Proforma"
                            >
                              <FileText className="w-5 h-5" />
                            </button>
                          )}
                           {invoice.status !== 'paid' && (isAdmin || isManager || isBilling || isAccountant || isCashier) && (
                            <button 
                              onClick={() => handleSingleMarkAsPaid(invoice)}
                              className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all animate-pulse"
                              title="Mark as Paid"
                            >
                              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            </button>
                          )}
                          <button 
                            onClick={() => openModal(invoice)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                            title="Edit Invoice"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          {(isAdmin || isManager) && (
                            <button 
                              onClick={() => handleDelete(invoice.id!)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                              title="Delete Invoice"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredInvoices.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3 text-slate-400">
                      <Receipt className="w-12 h-12 opacity-20" />
                      <p className="font-bold italic">No invoices found matching your criteria</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {hasMoreInvoices && filteredInvoices.length >= invoiceLimit && (
            <div className="flex justify-center p-6 bg-slate-50/50 border-t border-slate-100">
              <button
                onClick={() => setInvoiceLimit(prev => prev + 25)}
                className="px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-2xl font-bold text-sm transition-all shadow-sm flex items-center gap-2 cursor-pointer"
              >
                <span>Load More Invoices ({invoiceLimit} currently loaded)</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )}

  {/* Agent Filter Modal */}
      <AnimatePresence>
        {isAgentFilterOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsAgentFilterOpen(false)}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-sm bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-black text-slate-900">Sales Agents</h3>
                  <button 
                    onClick={() => setIsAgentFilterOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>
                <p className="text-xs font-bold text-slate-400 uppercase mt-1 tracking-wider">Select agents to filter</p>
              </div>

              <div className="p-4 max-h-[400px] overflow-y-auto">
                <div className="space-y-1">
                  {billingUsers.map(agent => (
                    <button
                      key={agent.uid}
                      onClick={() => {
                        setSelectedAgentIds(prev => 
                          prev.includes(agent.uid) 
                            ? prev.filter(id => id !== agent.uid)
                            : [...prev, agent.uid]
                        );
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-2xl transition-all",
                        selectedAgentIds.includes(agent.uid)
                          ? "bg-blue-50 text-blue-900"
                          : "hover:bg-slate-50 text-slate-600"
                      )}
                    >
                      <div className={cn(
                        "w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all",
                        selectedAgentIds.includes(agent.uid)
                          ? "bg-blue-600 border-blue-600"
                          : "border-slate-200"
                      )}>
                        {selectedAgentIds.includes(agent.uid) && <Check className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-bold text-sm">{agent.displayName || agent.email}</p>
                        <p className="text-[10px] opacity-60 uppercase tracking-widest">{agent.email}</p>
                      </div>
                    </button>
                  ))}
                  {billingUsers.length === 0 && (
                    <div className="py-12 text-center text-slate-400">
                      <Users className="w-12 h-12 mx-auto opacity-20 mb-3" />
                      <p className="text-xs font-bold uppercase">No billing users found</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-slate-50 border-t border-slate-100">
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedAgentIds([])}
                    className="flex-1 py-3 px-4 rounded-2xl font-bold tracking-widest uppercase text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => setIsAgentFilterOpen(false)}
                    className="flex-1 py-3 px-4 bg-blue-900 text-white rounded-2xl font-bold tracking-widest uppercase text-xs hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
                  >
                    Show Results
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

  {/* View Invoice Modal */}
      <AnimatePresence>
        {isViewModalOpen && viewingInvoice && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsViewModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-4xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
            >
              {viewingInvoice.billedUnderInvoiceNo && (
                <div className="bg-emerald-50 border-b border-emerald-100 p-4 flex items-center gap-3">
                  <Receipt className="w-5 h-5 text-emerald-600" />
                  <p className="text-emerald-800 font-bold text-sm">
                    This Proforma Invoice has been billed under Tax Invoice No. <span className="underline font-black">#{viewingInvoice.billedUnderInvoiceNo}</span>
                  </p>
                </div>
              )}
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                      {viewingInvoice.type === 'proforma' ? 'Proforma Invoice' : 'Invoice'} #{viewingInvoice.invoiceNumber}
                    </h2>
                    <p className="text-slate-500 text-sm font-medium">Preview document before printing</p>
                  </div>
                </div>
                 <div className="flex items-center gap-3">
                  {viewingInvoice.status !== 'paid' && (isAdmin || isManager || isBilling || isAccountant || isCashier) && (
                    <button
                      onClick={() => handleSingleMarkAsPaid(viewingInvoice)}
                      className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      Mark Paid
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      const iframe = document.getElementById('pdf-preview-iframe') as HTMLIFrameElement;
                      if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.print();
                      } else {
                        await generateInvoicePDF(viewingInvoice);
                      }
                    }}
                    className="flex items-center gap-2 bg-blue-800 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200"
                  >
                    <Printer className="w-5 h-5" />
                    Print / Download
                  </button>
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="p-2.5 hover:bg-slate-100 rounded-xl transition-colors text-slate-400"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content - Scrollable Invoice Preview */}
              <div className="flex-1 overflow-y-auto p-8 bg-slate-100/50">
                <div 
                  className="mx-auto bg-white shadow-2xl border border-slate-200 relative overflow-hidden"
                  style={{ 
                    width: '210mm', 
                    minHeight: '297mm',
                    fontSize: `${layoutSettings.fontSize}pt`,
                    fontFamily: layoutSettings.fontFamily,
                  }}
                >
                  {/* Custom Elements Rendered as absolute overlays */}
                  {layoutSettings.customElements?.map(el => (
                    <div
                      key={el.id}
                      style={{
                        position: 'absolute',
                        left: `${el.x}mm`,
                        top: `${el.y}mm`,
                        width: el.width ? `${el.width}mm` : 'auto',
                        height: el.height ? `${el.height}mm` : 'auto',
                        opacity: el.opacity ?? 1,
                        zIndex: 5
                      }}
                    >
                      {el.type === 'rect' && (
                        <div style={{ 
                          width: '100%', 
                          height: '100%', 
                          backgroundColor: el.fill, 
                          border: el.stroke ? `${el.strokeWidth || 0.1}mm solid ${el.stroke}` : 'none' 
                        }} />
                      )}
                      {el.type === 'line' && el.points && (
                         <div style={{ 
                            position: 'absolute',
                            width: `${Math.abs(el.points[2] - el.points[0])}mm`,
                            height: `${el.strokeWidth || 0.2}mm`,
                            backgroundColor: el.stroke || '#000',
                            transform: `rotate(${Math.atan2(el.points[3] - el.points[1], el.points[2] - el.points[0])}rad)`,
                            transformOrigin: '0 0'
                         }} />
                      )}
                      {el.type === 'text' && (
                        <div style={{ 
                          color: el.fill || '#000', 
                          fontSize: `${el.fontSize || 10}pt`, 
                          fontWeight: el.fontStyle?.includes('bold') ? 'bold' : 'normal',
                          fontStyle: el.fontStyle?.includes('italic') ? 'italic' : 'normal',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {el.text}
                        </div>
                      )}
                      {el.type === 'image' && (
                        <div style={{ width: '100%', height: '100%' }}>
                          {el.id.includes('sign') && (viewingInvoice.signatureUrl || signatureUrl) ? (
                            <img 
                              src={viewingInvoice.signatureUrl || signatureUrl || ''} 
                              alt="Signature" 
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div style={{ 
                              width: '100%', 
                              height: '100%', 
                              border: '1px dashed #cbd5e1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f8fafc'
                            }}>
                               <ImageIcon className="w-4 h-4 text-slate-300" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Custom Grid Sections */}
                  {layoutSettings.sections?.map(sec => (
                    <div
                      key={sec.id}
                      style={{
                        position: 'absolute',
                        left: `${sec.x}mm`,
                        top: `${sec.y}mm`,
                        width: `${sec.width}mm`,
                        height: `${sec.height}mm`,
                        display: 'grid',
                        gridTemplateRows: `repeat(${sec.rows}, 1fr)`,
                        gridTemplateColumns: `repeat(${sec.cols}, 1fr)`,
                        border: sec.style?.border ? '1px solid #e2e8f0' : 'none',
                        backgroundColor: sec.style?.backgroundColor || 'transparent',
                        fontSize: `${sec.style?.fontSize || layoutSettings.fontSize}pt`,
                        zIndex: 10
                      }}
                    >
                      {Array.from({ length: sec.rows }).map((_, r) => (
                        Array.from({ length: sec.cols }).map((_, c) => {
                          const key = `${r}-${c}`;
                          const field = sec.fields?.[key];
                          let val = '';
                          if (field) {
                             if (field === 'invoiceNumber') val = viewingInvoice.invoiceNumber;
                             else if (field === 'createdAt') val = formatLongDate(viewingInvoice.createdAt);
                             else if (field === 'dueDate') val = formatLongDate(viewingInvoice.dueDate);
                             else if (field === 'clientName') val = viewingInvoice.clientName;
                             else if (field === 'totalAmount') val = formatCurrency(viewingInvoice.totalAmount, viewingInvoice.currency);
                             else if (field === 'notes') val = viewingInvoice.notes || '';
                             else if (field === 'signatoryName') val = viewingInvoice.signatoryName || '';
                             else if (field === 'signatoryTitle') val = viewingInvoice.signatoryTitle || '';
                             else if (field === 'smNumber') val = viewingInvoice.smNumber || '';
                             else if (field === 'lpoNumber') val = viewingInvoice.lpoNumber || '';
                             else if (field === 'deliveryNote') val = viewingInvoice.deliveryNote || '';
                          }
                          return (
                            <div key={key} style={{ padding: '2px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: sec.style?.textAlign || 'left', border: '0.1px solid rgba(0,0,0,0.05)' }}>
                              <span className="text-[10px] font-bold">{val}</span>
                            </div>
                          );
                        })
                      ))}
                    </div>
                  ))}

                  <div style={{ padding: `${layoutSettings.marginTop}mm ${layoutSettings.marginRight}mm ${layoutSettings.marginBottom}mm ${layoutSettings.marginLeft}mm` }}>
                    {/* Watermark for Proforma */}
                    {viewingInvoice.type === 'proforma' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] rotate-[-45deg]">
                        <span className="text-[150px] font-black">PROFORMA</span>
                      </div>
                    )}

                    {/* Invoice Header */}
                    <div className="flex justify-between items-start mb-12 border-b-4 border-blue-800 pb-8 relative z-10">
                      <div className="space-y-4">
                        {logoUrl ? (
                          <img src={logoUrl} alt="Logo" style={{ height: `${layoutSettings.logoWidth || 35}mm` }} className="object-contain" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="space-y-1">
                            <h1 className="text-2xl font-black text-blue-800">BRIGHT TECHNICAL SYSTEMS</h1>
                            <p className="text-xs font-bold text-orange-500 tracking-widest uppercase">General Supplies Limited</p>
                          </div>
                        )}
                        <div className="text-[10px] text-slate-500 space-y-0.5 font-medium">
                          <p>123 Tech Avenue, Innovation City</p>
                          <p>contact@bts.com | +1 (555) 000-1111</p>
                          <p>TIN: 123-456-789 | VRN: 987-654-321</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <h2 className="text-5xl font-black text-slate-900 mb-2 tracking-tighter">
                          {viewingInvoice.type === 'proforma' ? 'PROFORMA' : 'INVOICE'}
                        </h2>
                        <div className="inline-block px-4 py-1 bg-slate-900 text-white rounded-lg text-sm font-bold">
                          #{viewingInvoice.invoiceNumber}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-12 mb-12 relative z-10">
                      <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Bill To:</h3>
                        <div className="space-y-2">
                          <p className="font-black text-slate-900 text-xl leading-tight">{viewingInvoice.clientName}</p>
                          {viewingInvoice.branchName && (
                            <div className="flex items-center gap-2 text-blue-800 font-bold text-sm">
                              <Building2 className="w-4 h-4" />
                              {viewingInvoice.branchName}
                            </div>
                          )}
                          {clients.find(c => c.id === viewingInvoice.clientId)?.address && (
                            <p className="text-slate-500 text-sm leading-relaxed">
                              {clients.find(c => c.id === viewingInvoice.clientId)?.address}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col justify-center items-end space-y-6">
                        <div className="text-right">
                          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Date Issued:</h3>
                          <p className="font-black text-slate-900 text-lg">{safeToDate(viewingInvoice.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                        </div>
                        <div className="text-right">
                          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Due Date:</h3>
                          <p className="font-black text-blue-800 text-lg">{safeToDate(viewingInvoice.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                        </div>
                        {viewingInvoice.type === 'tax' ? (
                          <>
                            <div className="text-right">
                              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">SMNO:</h3>
                              <p className="font-black text-slate-900 text-lg">{viewingInvoice.smNumber || ''}</p>
                            </div>
                            <div className="text-right">
                              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">LPO NO:</h3>
                              <p className="font-black text-slate-900 text-lg">{viewingInvoice.lpoNumber || ''}</p>
                            </div>
                            <div className="text-right">
                              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">D/NOTE:</h3>
                              <p className="font-black text-slate-900 text-lg">{viewingInvoice.deliveryNote || ''}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-right">
                              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Sales Agent:</h3>
                              <p className="font-black text-slate-900 text-lg">{viewingInvoice.salesAgent || 'JACKSON MANDELA'}</p>
                            </div>
                            {viewingInvoice.agentPhone && (
                              <div className="text-right">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Agent Phone:</h3>
                                <p className="font-bold text-slate-600 text-sm">{viewingInvoice.agentPhone}</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {viewingInvoice.description && (
                      <div className="mb-2.5 p-4 bg-slate-50 border border-slate-200 rounded-2xl relative z-10 w-fit max-w-full mx-auto text-center">
                        <h3 className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Subject:</h3>
                        <p className="font-bold text-slate-900 text-base">RE: {viewingInvoice.description.toUpperCase()}</p>
                      </div>
                    )}

                    {/* Items Table */}
                    <div className="mb-12 relative z-10">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-900 text-white">
                            <th className="p-4 text-[10px] font-black uppercase tracking-widest rounded-l-xl">S/N</th>
                            <th className="p-4 text-[10px] font-black uppercase tracking-widest">Description</th>
                            <th className="p-4 text-[10px] font-black uppercase tracking-widest text-center">Qty</th>
                            <th className="p-4 text-[10px] font-black uppercase tracking-widest text-right">Unit Price</th>
                            <th className="p-4 text-[10px] font-black uppercase tracking-widest text-right rounded-r-xl">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {viewingInvoice.items.map((item, idx) => (
                            <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                              <td className="p-4 text-sm font-bold text-slate-400">{item.serialNumber || ''}</td>
                              <td className="p-4">
                                <p className="text-sm font-bold text-slate-900">{item.description}</p>
                              </td>
                              <td className="p-4 text-sm font-bold text-slate-600 text-center">{item.quantity} {item.unit || 'pcs'}</td>
                              <td className="p-4 text-sm font-bold text-slate-600 text-right">
                                {viewingInvoice.currency || defaultCurrency} {item.unitPrice.toLocaleString()}
                              </td>
                              <td className="p-4 text-sm font-black text-slate-900 text-right">
                                {viewingInvoice.currency || defaultCurrency} {item.amount.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Totals Section */}
                    <div className="flex justify-between items-start gap-12 relative z-10">
                      <div className="flex-1 space-y-6">
                        {bankAccounts.find(a => a.id === viewingInvoice.bankAccountId) && (
                          <div className="p-6 bg-blue-50/50 rounded-3xl border border-blue-100">
                            <h3 className="text-[10px] font-black text-blue-800 uppercase tracking-[0.2em] mb-3">Payment Details:</h3>
                            <div className="space-y-1 text-sm">
                              <p className="font-bold text-slate-900">Bank: {bankAccounts.find(a => a.id === viewingInvoice.bankAccountId)?.name}</p>
                              {bankAccounts.find(a => a.id === viewingInvoice.bankAccountId)?.branchCode && (
                                <p className="text-slate-600">Branch Code: <span className="font-bold">{bankAccounts.find(a => a.id === viewingInvoice.bankAccountId)?.branchCode}</span></p>
                              )}
                              <p className="text-slate-600">Account Name: {bankAccounts.find(a => a.id === viewingInvoice.bankAccountId)?.accountName || 'Bright Technical Systems'}</p>
                              <p className="text-slate-600 font-mono">Account No: {bankAccounts.find(a => a.id === viewingInvoice.bankAccountId)?.accountNumber}</p>
                              <p className="text-slate-600">Currency: {bankAccounts.find(a => a.id === viewingInvoice.bankAccountId)?.currency}</p>
                            </div>
                          </div>
                        )}
                        <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100">
                          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2">Amount in Words:</h3>
                          <p className="text-sm font-bold text-slate-700 italic">
                            {numberToWords(viewingInvoice.totalAmount)} {(viewingInvoice.currency || defaultCurrency).charAt(0) + (viewingInvoice.currency || defaultCurrency).slice(1).toLowerCase()} Only.
                          </p>
                        </div>
                      </div>

                      <div className="w-80 space-y-4">
                        <div className="flex justify-between text-sm px-4">
                          <span className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">Subtotal</span>
                          <span className="font-black text-slate-900">
                            {viewingInvoice.currency || defaultCurrency} {calculateSubtotal(viewingInvoice.items).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm px-4">
                          <span className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">VAT ({viewingInvoice.vatRate || vatRate}%)</span>
                          <span className="font-black text-slate-900">
                            {viewingInvoice.currency || defaultCurrency} {(viewingInvoice.vatAmount || (calculateSubtotal(viewingInvoice.items) * (viewingInvoice.vatRate || vatRate) / 100)).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-6 bg-blue-800 rounded-[2rem] text-white shadow-xl shadow-blue-200">
                          <span className="font-black uppercase tracking-widest text-xs">Total Amount</span>
                          <span className="text-2xl font-black">
                            {viewingInvoice.currency || defaultCurrency} {viewingInvoice.totalAmount.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Notes & Terms */}
                    <div className="mt-12 grid grid-cols-2 gap-12 relative z-10">
                      <div className="space-y-4">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Terms & Conditions:</h3>
                        <ul className="text-[10px] text-slate-500 space-y-1 list-disc pl-4 font-medium">
                          <li>Please include invoice number as payment reference.</li>
                          <li>Payments should be made within 14 days of issue.</li>
                          <li>Goods remain property of BTS until fully paid.</li>
                        </ul>
                      </div>
                      {!layoutSettings.customElements?.some(el => el.id.includes('sign')) && (
                        <div className="flex flex-col items-center justify-end">
                          {((viewingInvoice.signatureUrl || signatureUrl) && viewingInvoice.type !== 'tax') && (
                            <img 
                              src={viewingInvoice.signatureUrl || signatureUrl || ''} 
                              alt="Signature" 
                              className="h-16 mb-2 object-contain"
                            />
                          )}
                          <div className="w-48 h-px bg-slate-900 mb-2" />
                          <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest leading-none mb-1">
                            {viewingInvoice.signatoryName || (viewingInvoice.type === 'tax' ? 'Fabian Kisaki' : (profile?.displayName || 'Authorized Signatory'))}
                          </p>
                          <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest leading-none">
                            {viewingInvoice.signatoryTitle || (viewingInvoice.type === 'tax' ? 'Managing Director' : (profile?.role || 'Company Representative'))}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-4xl bg-white rounded-[2.5rem] shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
                    <Receipt className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                      {editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}
                    </h2>
                    <p className="text-slate-500 font-medium italic">Fill in the details for the client invoice</p>
                  </div>
                </div>
                <button onClick={closeModal} className="p-3 hover:bg-slate-100 rounded-2xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Client</label>
                      <select
                        value={formData.clientId}
                        onChange={(e) => {
                          setFormData({ ...formData, clientId: e.target.value, branchId: '' });
                          setBillForGeneralService(false);
                        }}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                      >
                        <option value="">Select a client...</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>{client.name}</option>
                        ))}
                      </select>
                    </div>

                    {formData.clientId && isServiceClient && hasGeneralService && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="col-span-full bg-blue-50/50 border border-blue-100/50 p-4 rounded-2xl flex items-center justify-between"
                      >
                        <div>
                          <p className="text-xs font-black text-blue-900 uppercase tracking-widest mb-1">General Service Billing Available</p>
                          <p className="text-xs text-slate-500 font-medium">Would you like to bill this client for their pre-configured General Service prices?</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={billForGeneralService}
                            onChange={(e) => handleToggleGeneralService(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-800"></div>
                        </label>
                      </motion.div>
                    )}

                    {formData.clientId && clientBranches.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-2"
                      >
                        <label className="text-sm font-semibold text-slate-700">Branch</label>
                        <select
                          value={formData.branchId}
                          onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                        >
                          <option value="">Select a branch...</option>
                          {clientBranches.map(branch => (
                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                          ))}
                        </select>
                      </motion.div>
                    )}

                    {formData.clientId && (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-2"
                      >
                        <label className="text-sm font-semibold text-slate-700">Service Type Associated *</label>
                        <select
                          required
                          value={formData.serviceType}
                          onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                        >
                          <option value="">Select service type...</option>
                          {(() => {
                            const client = clients.find(c => c.id === formData.clientId);
                            const clientServices = client?.serviceType ? client.serviceType.split(',').map((s: string) => s.trim()) : [];
                            const list = clientServices.length > 0 ? clientServices : ['Service', 'Repair', 'Supply and Installation'];
                            return list.map((s: string) => (
                              <option key={s} value={s}>{s}</option>
                            ));
                          })()}
                        </select>
                      </motion.div>
                    )}
                  </div>

                  {formData.clientId && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-8"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">
                            {formData.type === 'proforma' ? 'PFI Number' : 'Invoice Number'}
                          </label>
                          <input
                            type="text"
                            value={formData.invoiceNumber}
                            onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Invoice Type</label>
                          <select
                            value={formData.type}
                            onChange={(e) => {
                              const newType = e.target.value as any;
                              const d = new Date();
                              let newDueDate = formData.dueDate;
                              let newInvoiceNumber = formData.invoiceNumber;

                              if (newType === 'proforma') {
                                d.setMonth(d.getMonth() + 2);
                                newDueDate = d.toISOString().split('T')[0];
                              } else if (newType === 'tax') {
                                d.setMonth(d.getMonth() + 1);
                                newDueDate = d.toISOString().split('T')[0];
                              } else {
                                d.setMonth(d.getMonth() + 1);
                                newDueDate = d.toISOString().split('T')[0];
                              }
                              // Use sequence based on selected type
                              newInvoiceNumber = getNextInvoiceNumber();

                              setFormData({ 
                                ...formData, 
                                type: newType,
                                dueDate: newDueDate,
                                invoiceNumber: newInvoiceNumber
                              });
                            }}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          >
                            <option value="formal">Normal Invoice</option>
                            <option value="tax">Tax Invoice</option>
                            <option value="proforma">Proforma Invoice</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Design Template</label>
                          <select
                            value={formData.template}
                            onChange={(e) => setFormData({ ...formData, template: e.target.value as any })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          >
                            <option value="standard">Standard (Default)</option>
                            <option value="modern">Modern (Clean)</option>
                            <option value="classic">Classic (Professional)</option>
                          </select>
                        </div>
                        {formData.type !== 'proforma' && (
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Account</label>
                            <select
                              value={formData.bankAccountId}
                              onChange={(e) => setFormData({ ...formData, bankAccountId: e.target.value })}
                              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                            >
                              <option value="">Select Bank Account...</option>
                              {bankAccounts.map(acc => (
                                <option key={acc.id} value={acc.id}>{acc.name} ({acc.currency})</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {formData.type === 'tax' ? (
                          <>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-700">SMNO.</label>
                              <input
                                type="text"
                                value={formData.smNumber}
                                onChange={(e) => setFormData({ ...formData, smNumber: e.target.value })}
                                placeholder="Service Memo Number"
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-700">LPO NO.</label>
                              <input
                                type="text"
                                value={formData.lpoNumber}
                                onChange={(e) => setFormData({ ...formData, lpoNumber: e.target.value })}
                                placeholder="Local Purchase Order Number"
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-700">D/NOTE</label>
                              <input
                                type="text"
                                value={formData.deliveryNote}
                                onChange={(e) => setFormData({ ...formData, deliveryNote: e.target.value })}
                                placeholder="Delivery Note"
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-700">Sales Agent</label>
                              <input
                                type="text"
                                value={formData.salesAgent}
                                onChange={(e) => setFormData({ ...formData, salesAgent: e.target.value })}
                                placeholder="e.g. JACKSON MANDELA"
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-700">Agent Phone</label>
                              <input
                                type="text"
                                value={formData.agentPhone}
                                onChange={(e) => setFormData({ ...formData, agentPhone: e.target.value })}
                                placeholder="e.g. 0689460993"
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                              />
                            </div>
                          </>
                        )}
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Due Date</label>
                          <input
                            type="date"
                            value={formData.dueDate}
                            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Font Family</label>
                          <select
                            value={formData.fontFamily}
                            onChange={(e) => setFormData({ ...formData, fontFamily: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          >
                            <option value="helvetica">Helvetica</option>
                            <option value="times">Times New Roman</option>
                            <option value="courier">Courier</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Font Size</label>
                          <input
                            type="number"
                            min="6"
                            max="14"
                            value={formData.fontSize}
                            onChange={(e) => setFormData({ ...formData, fontSize: parseInt(e.target.value) })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Font Style</label>
                          <select
                            value={formData.fontStyle}
                            onChange={(e) => setFormData({ ...formData, fontStyle: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          >
                            <option value="normal">Normal</option>
                            <option value="italic">Italic</option>
                            <option value="bold">Bold</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Link to Approved Report (Optional)</label>
                          <select
                            value={formData.reportId}
                            onChange={(e) => handleReportSelect(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                          >
                            <option value="">Select a report...</option>
                            {approvedReports.map(report => (
                              <option key={report.id} value={report.id}>
                                {report.clientName} - {report.type.toUpperCase()} ({report.id.substring(0, 8)})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Description</label>
                          <textarea
                            ref={descriptionRef}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all text-center font-bold font-sans"
                            placeholder="RE: INVOICE DESCRIPTION"
                            rows={1}
                            style={{ resize: 'none', overflowY: 'hidden' }}
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-slate-700">Notes</label>
                          <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                            rows={2}
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-slate-900 flex items-center gap-2 text-lg">
                            <DollarSign className="w-5 h-5 text-blue-800" />
                            Invoice Items
                          </h3>
                          <button
                            type="button"
                            onClick={handleAddItem}
                            className="text-blue-800 hover:text-blue-900 font-bold text-sm flex items-center gap-1 bg-blue-50 px-4 py-2 rounded-xl transition-all"
                          >
                            <PlusCircle className="w-4 h-4" />
                            Add Item
                          </button>
                        </div>

                        <div className="space-y-3">
                          {formData.items.map((item, index) => (
                            <div key={index} className="grid grid-cols-12 gap-3 items-end p-4 bg-slate-50 rounded-2xl border border-slate-100">
                              <div className="col-span-12 md:col-span-1 space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">S/N</label>
                                <input
                                  type="text"
                                  value={item.serialNumber || ''}
                                  onChange={(e) => handleItemChange(index, 'serialNumber', e.target.value)}
                                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                                  placeholder="S/N"
                                />
                              </div>
                              <div className="col-span-12 md:col-span-4 space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">Description</label>
                                <div className="relative group/desc">
                                  <input
                                    type="text"
                                    list={`spare-parts-${index}`}
                                    value={item.description}
                                    onChange={(e) => {
                                      const selectedPart = clientSpareParts.find(p => p.name === e.target.value);
                                      if (selectedPart) {
                                        handleItemChange(index, 'description', selectedPart.name);
                                        handleItemChange(index, 'unitPrice', selectedPart.price || '');
                                      } else {
                                        handleItemChange(index, 'description', e.target.value);
                                      }
                                    }}
                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm pr-10 focus:ring-2 focus:ring-blue-800/10 transition-all"
                                    placeholder="Service or part description"
                                  />
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActiveItemIndex(index);
                                        setIsPartSelectorOpen(true);
                                      }}
                                      className="p-1 hover:bg-blue-50 text-slate-400 hover:text-blue-800 rounded-lg transition-colors cursor-pointer"
                                      title="Select Spare Part"
                                    >
                                      <Wrench className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <datalist id={`spare-parts-${index}`}>
                                    {clientSpareParts.map((part, pIdx) => (
                                      <option key={pIdx} value={part.name} />
                                    ))}
                                  </datalist>
                                </div>
                              </div>
                              <div className="col-span-4 md:col-span-1 space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">Qty</label>
                                <input
                                  type="text"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    // Allow only numbers
                                    if (val === '' || /^\d+$/.test(val)) {
                                      handleItemChange(index, 'quantity', val === '' ? '' : parseInt(val));
                                    }
                                  }}
                                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                                  placeholder="0"
                                />
                              </div>
                              <div className="col-span-4 md:col-span-1 space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">Unit</label>
                                <input
                                  type="text"
                                  value={item.unit || ''}
                                  onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                                  placeholder="Unit"
                                />
                              </div>
                              <div className="col-span-4 md:col-span-2 space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">Unit Price</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unitPrice}
                                  onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                                />
                              </div>
                              <div className="col-span-4 md:col-span-1 space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">VAT</label>
                                <select
                                  value={item.vatStatus || 'VAT'}
                                  onChange={(e) => handleItemChange(index, 'vatStatus', e.target.value)}
                                  className="w-full px-2 py-2 bg-white border border-slate-200 rounded-xl text-[10px] font-bold"
                                >
                                  <option value="VAT">VAT</option>
                                  <option value="No VAT">No VAT</option>
                                </select>
                              </div>
                              <div className="col-span-3 md:col-span-2 space-y-1">
                                <label className="text-[10px] font-bold text-slate-400 uppercase">Amount</label>
                                <div className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-sm font-bold text-slate-600">
                                  {clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency} {item.amount === '' ? '0' : Number(item.amount).toLocaleString()}
                                </div>
                              </div>
                              <div className="col-span-1 flex justify-end">
                                <button 
                                  type="button" 
                                  onClick={() => handleRemoveItem(index)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                  disabled={formData.items.length === 1}
                                >
                                  <MinusCircle className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="flex justify-end pt-4">
                          <div className="bg-slate-900 text-white px-8 py-4 rounded-2xl shadow-xl">
                            <div className="flex justify-between gap-8 mb-1">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Subtotal</p>
                              <p className="text-sm font-bold">{clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency} {calculateSubtotal(formData.items).toLocaleString()}</p>
                            </div>
                            <div className="flex justify-between gap-8 mb-2 pb-2 border-b border-white/10">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">VAT ({vatRate}%)</p>
                              <p className="text-sm font-bold">{clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency} {calculateVatAmount(formData.items, vatRate).toLocaleString()}</p>
                            </div>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 text-right">Total Amount</p>
                            <p className="text-3xl font-black text-right">
                              {clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency} {(calculateSubtotal(formData.items) + calculateVatAmount(formData.items, vatRate)).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="pt-6 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-blue-800" />
                            <h3 className="font-bold text-slate-800 uppercase tracking-wider text-xs">Signatory Details</h3>
                          </div>
                          {signatories.length > 0 && (
                            <select
                              onChange={(e) => {
                                const selected = signatories.find(s => s.id === e.target.value);
                                if (selected) {
                                  setFormData({
                                    ...formData,
                                    signatoryName: selected.name,
                                    signatoryTitle: selected.title,
                                    signatureUrl: selected.signatureUrl || ''
                                  });
                                }
                              }}
                              className="text-xs font-bold text-blue-800 bg-blue-50 px-3 py-1 rounded-lg border-none focus:ring-0 cursor-pointer"
                            >
                              <option value="">Select Pre-defined Signatory...</option>
                              {signatories.map(sig => (
                                <option key={sig.id} value={sig.id}>{sig.name} ({sig.title})</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Authorized Signatory Name</label>
                            <input
                              type="text"
                              value={formData.signatoryName}
                              onChange={(e) => setFormData({ ...formData, signatoryName: e.target.value })}
                              placeholder="e.g. Fabian Kisaki"
                              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Signatory Title</label>
                            <input
                              type="text"
                              value={formData.signatoryTitle}
                              onChange={(e) => setFormData({ ...formData, signatoryTitle: e.target.value })}
                              placeholder="e.g. Managing Director"
                              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                            />
                          </div>
                          <div className="md:col-span-2 space-y-2">
                            <label className="text-sm font-semibold text-slate-700">Signature Image (Override Default)</label>
                            <div className="flex items-center gap-4">
                              <label className="flex-1 flex items-center justify-center h-20 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50 transition-all">
                                <div className="text-center">
                                  <PlusCircle className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                                  <p className="text-[10px] text-slate-500">Upload Signature</p>
                                </div>
                                <input 
                                  type="file" 
                                  className="hidden" 
                                  accept="image/*" 
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        setFormData({ ...formData, signatureUrl: reader.result as string });
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                              </label>
                              {formData.signatureUrl && (
                                <div className="relative w-24 h-20 bg-white border border-slate-200 rounded-2xl overflow-hidden flex items-center justify-center">
                                  <img src={formData.signatureUrl} alt="Signature" className="w-full h-full object-contain" />
                                  <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, signatureUrl: '' })}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white p-1 rounded-full shadow-lg"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">Status</label>
                        <div className="flex flex-wrap gap-3">
                          {['draft', 'sent', 'paid', 'overdue', 'cancelled'].map((s) => (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setFormData({ ...formData, status: s as any })}
                              className={`px-6 py-3 rounded-2xl text-sm font-bold uppercase tracking-wider transition-all ${
                                formData.status === s 
                                  ? 'bg-blue-800 text-white shadow-lg shadow-blue-200' 
                                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>

                        <div className="flex flex-col items-end space-y-2 pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-8 text-sm text-slate-500 font-medium">
                            <span>Subtotal:</span>
                            <span className="font-bold text-slate-900">
                              {clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency} {calculateSubtotal(formData.items).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-8 text-sm text-slate-500 font-medium">
                            <span>VAT ({vatRate}%):</span>
                            <span className="font-black text-slate-900">
                              {clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency} {(calculateSubtotal(formData.items) * vatRate / 100).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-8 text-xl font-black text-blue-800 pt-2 border-t border-slate-100 w-full md:w-auto justify-between md:justify-end">
                            <span>Total Amount:</span>
                            <span>
                              {clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency} {(calculateSubtotal(formData.items) * (1 + vatRate / 100)).toLocaleString()}
                            </span>
                          </div>
                          <div className="text-right w-full">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Amount in Words</p>
                            <p className="text-sm font-bold text-slate-600 italic">
                              {numberToWords(calculateSubtotal(formData.items) * (1 + vatRate / 100))} {(clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency).charAt(0) + (clients.find(c => c.id === formData.clientId)?.currency || defaultCurrency).slice(1).toLowerCase()} Only
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4 pt-4">
                        <button
                          type="button"
                          onClick={async () => {
                            const subtotal = calculateSubtotal(formData.items);
                            const vatAmount = (subtotal * vatRate) / 100;
                            const totalAmount = subtotal + vatAmount;
                            const client = clients.find(c => c.id === formData.clientId);
                            
                            const previewInvoice: Invoice = {
                              id: 'preview',
                              invoiceNumber: formData.invoiceNumber,
                              clientId: formData.clientId,
                              clientName: client?.name || 'Preview Client',
                              branchId: formData.branchId,
                              branchName: clientBranches.find(b => b.id === formData.branchId)?.name || '',
                              currency: client?.currency || defaultCurrency,
                              reportId: formData.reportId,
                              items: formData.items,
                              description: formData.description,
                              notes: formData.notes,
                              vatRate,
                              vatAmount,
                              totalAmount,
                              type: formData.type,
                              template: formData.template,
                              status: formData.status,
                              dueDate: Timestamp.fromDate(new Date(formData.dueDate)),
                              createdAt: Timestamp.now(),
                              updatedAt: Timestamp.now(),
                              salesAgent: formData.salesAgent,
                              agentPhone: formData.agentPhone,
                              bankAccountId: formData.bankAccountId,
                              signatoryName: formData.signatoryName,
                              signatoryTitle: formData.signatoryTitle,
                              signatureUrl: formData.signatureUrl
                            };
                            
                            const blob = await generateInvoicePDF(previewInvoice, 'blob') as unknown as Blob;
                            const url = URL.createObjectURL(blob);
                            setPreviewUrl(url);
                            setViewingInvoice(previewInvoice);
                            setIsPreviewModalOpen(true);
                          }}
                          className="flex-1 px-6 py-4 bg-slate-100 text-slate-700 rounded-2xl font-bold hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
                        >
                          <Eye className="w-5 h-5" />
                          Preview
                        </button>
                        <button
                          type="button"
                          onClick={closeModal}
                          className="flex-1 px-6 py-4 border border-slate-200 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 px-6 py-4 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                        >
                          {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingInvoice ? 'Update Invoice' : 'Create Invoice')}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Spare Part Selection Modal */}
      <AnimatePresence>
        {isPartSelectorOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsPartSelectorOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8 flex flex-col max-h-[80vh]"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-black text-slate-900">Select Client Spare Part</h3>
                <button onClick={() => setIsPartSelectorOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="relative mb-6">
                <Wrench className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search spare parts..."
                  value={partSearchTerm}
                  onChange={(e) => setPartSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                />
              </div>

              <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                {clientSpareParts
                  .filter(p => !partSearchTerm || p.name.toLowerCase().includes(partSearchTerm.toLowerCase()))
                      .map((part, pIdx) => (
                        <button
                          key={pIdx}
                          onClick={() => {
                            if (activeItemIndex !== null) {
                              handleItemChange(activeItemIndex, 'description', part.name);
                              handleItemChange(activeItemIndex, 'unitPrice', part.price || 0);
                              handleItemChange(activeItemIndex, 'unit', part.unit || 'pc');
                            }
                            setIsPartSelectorOpen(false);
                            setPartSearchTerm('');
                          }}
                          className="w-full flex items-center justify-between p-4 hover:bg-blue-50 rounded-2xl border border-transparent hover:border-blue-100 transition-all group"
                        >
                          <div className="text-left">
                            <p className="font-bold text-slate-900 group-hover:text-blue-800">{part.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {part.price && <p className="text-[10px] text-slate-500 font-bold">Price: {part.price.toLocaleString()}</p>}
                              {part.unit && <span className="text-[9px] font-black text-blue-400 uppercase bg-blue-50 px-1.5 py-0.5 rounded transition-colors group-hover:bg-blue-100">{part.unit}</span>}
                            </div>
                          </div>
                          <PlusCircle className="w-5 h-5 text-slate-300 group-hover:text-blue-800" />
                        </button>
                      ))}
                {clientSpareParts.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-slate-400 font-bold italic">No spare parts found for this client.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Email Modal */}
      <AnimatePresence>
        {isEmailModalOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEmailModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl p-8 overflow-hidden"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Send Invoice via Email</h2>
                    <p className="text-slate-500 text-sm font-medium italic">Compose your message to the client</p>
                  </div>
                </div>
                <button onClick={() => setIsEmailModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">To</label>
                  <input
                    type="text"
                    value={emailData.to}
                    onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                    placeholder="client@example.com"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-medium"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">CC</label>
                  <input
                    type="text"
                    value={emailData.cc}
                    onChange={(e) => setEmailData({ ...emailData, cc: e.target.value })}
                    placeholder="other@example.com"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-medium"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Subject</label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-medium"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-widest">Message</label>
                  <textarea
                    value={emailData.body}
                    onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-medium"
                  />
                </div>

                {invoices.find(i => i.id === emailData.invoiceId)?.reportId && (
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <input
                      type="checkbox"
                      id="attachSurvey"
                      checked={attachSurveyReport}
                      onChange={(e) => setAttachSurveyReport(e.target.checked)}
                      className="w-5 h-5 rounded border-slate-300 text-blue-800 focus:ring-blue-800/20"
                    />
                    <label htmlFor="attachSurvey" className="text-sm font-bold text-slate-700 cursor-pointer">
                      Attach Technical Survey Report
                    </label>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setIsEmailModalOpen(false)}
                    className="flex-1 px-6 py-3 border border-slate-200 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      console.log('Send Email button clicked');
                      setIsSubmitting(true);
                      try {
                        const invoice = invoices.find(i => i.id === emailData.invoiceId);
                        if (!invoice) throw new Error('Invoice not found');
                        
                        console.log('Generating Invoice PDF...');
                        const pdfDataUri = await generateInvoicePDF(invoice, 'datauristring') as string;
                        const pdfBase64 = pdfDataUri.split(',')[1];
                        let surveyPdfBase64 = null;

                        if (attachSurveyReport && invoice.reportId) {
                          console.log('Generating Survey Report PDF...');
                          // Fetch report data
                          const reportSnap = await getDoc(doc(db, 'reports', invoice.reportId));
                          if (reportSnap.exists()) {
                            const report = { id: reportSnap.id, ...reportSnap.data() } as Report;
                            const surveyPdfDataUri = generateSurveyReportPDF(report, true) as string;
                            surveyPdfBase64 = surveyPdfDataUri.split(',')[1];
                          }
                        }
                        
                        console.log('Sending request to /api/send-invoice...');
                        const response = await fetch('/api/send-invoice', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            to: emailData.to,
                            cc: emailData.cc,
                            subject: emailData.subject,
                            body: emailData.body,
                            pdfBase64,
                            surveyPdfBase64,
                            fileName: `${invoice.type === 'proforma' ? 'Proforma' : 'Invoice'}_${invoice.invoiceNumber}.pdf`
                          })
                        });

                        const result = await response.json();
                        if (!response.ok) throw new Error(result.error || 'Failed to send email');

                        await updateDoc(doc(db, 'invoices', emailData.invoiceId), {
                          status: 'sent',
                          updatedAt: Timestamp.now()
                        });
                        toast.success('Email sent successfully');
                        setIsEmailModalOpen(false);
                      } catch (error: any) {
                        console.error('Email error:', error);
                        toast.error(error.message || 'Failed to send email');
                      } finally {
                        setIsSubmitting(false);
                      }
                    }}
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
                    {isSubmitting ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* PDF Preview Modal */}
      <AnimatePresence>
        {isPreviewModalOpen && previewUrl && viewingInvoice && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsPreviewModalOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-[95vw] lg:max-w-7xl h-[95vh] bg-white rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden"
            >
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-slate-900 tracking-tight">Invoice Preview</h2>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{viewingInvoice.invoiceNumber || 'Draft Invoice'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => { generateInvoicePDF(viewingInvoice); }}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 transition-all font-mono"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </button>
                  <button
                    onClick={() => {
                        const link = document.createElement('a');
                        link.href = previewUrl;
                        link.download = `Invoice-${viewingInvoice.invoiceNumber || 'Draft'}.pdf`;
                        link.click();
                    }}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 font-mono"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <button onClick={() => setIsPreviewModalOpen(false)} className="p-3 hover:bg-slate-100 rounded-2xl transition-colors ml-2">
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
              </div>

              <div className="flex-1 bg-slate-100 overflow-hidden flex justify-center p-4">
                <div className="w-full h-full bg-white rounded-2xl overflow-hidden shadow-inner">
                  <iframe
                    id="pdf-preview-iframe"
                    src={`${previewUrl}#view=FitH`}
                    className="w-full h-full border-none"
                    title="Invoice Preview"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel || "YES"}
        cancelLabel={confirmModal.cancelLabel || "NO"}
        isDanger={confirmModal.isDanger !== undefined ? confirmModal.isDanger : true}
      />

      <AnimatePresence>
        {bankAccountSelectInvoice && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setBankAccountSelectInvoice(null)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2rem] shadow-2xl overflow-hidden p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-black text-slate-900">Select Bank Account</h3>
                  <p className="text-xs text-slate-500 font-medium mt-1">Please select the Bank Account for this Tax Invoice</p>
                </div>
                <button
                  onClick={() => setBankAccountSelectInvoice(null)}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-wider">Invoice Details</label>
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-sm">
                    <p className="text-slate-600 font-medium">Invoice: <span className="font-bold text-slate-950">#{bankAccountSelectInvoice.invoiceNumber}</span></p>
                    <p className="text-slate-600 font-medium mt-1">Client: <span className="font-bold text-slate-950">{bankAccountSelectInvoice.clientName}</span></p>
                    <p className="text-slate-600 font-medium mt-1">Amount: <span className="font-black text-blue-900">{bankAccountSelectInvoice.currency || defaultCurrency} {bankAccountSelectInvoice.totalAmount.toLocaleString()}</span></p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-wider">Select Account</label>
                  <select
                    value={selectedBankAccountId}
                    onChange={(e) => setSelectedBankAccountId(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-slate-800 text-sm"
                  >
                    <option value="">Choose Bank Account...</option>
                    {bankAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name} ({acc.currency} {acc.balance.toLocaleString()})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-8">
                <button
                  onClick={() => setBankAccountSelectInvoice(null)}
                  className="px-6 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all text-sm cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  disabled={!selectedBankAccountId}
                  onClick={handleSaveBankAndMarkPaid}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-emerald-200 disabled:opacity-50 text-sm cursor-pointer"
                >
                  Mark as Paid
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Pending Box Drafts Recovery Modal */}
      <AnimatePresence>
        {isPendingBoxOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsPendingBoxOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl p-8 max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600">
                    <Inbox className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                      Pending Box
                    </h2>
                    <p className="text-slate-500 font-medium italic">Recover and continue creating your unsaved invoices</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {pendingDrafts.length > 0 && (
                    <button
                      onClick={clearAllDrafts}
                      className="px-4 py-2 bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 font-bold rounded-xl text-xs transition-all cursor-pointer flex items-center gap-1.5 border border-rose-100"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Clear All
                    </button>
                  )}
                  <button onClick={() => setIsPendingBoxOpen(false)} className="p-3 hover:bg-slate-100 rounded-2xl transition-colors">
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
              </div>

              {pendingDrafts.length === 0 ? (
                <div className="py-16 text-center space-y-4">
                  <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center text-slate-400 mx-auto">
                    <Inbox className="w-10 h-10 animate-bounce" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-slate-700">Your Pending Box is empty</p>
                    <p className="text-slate-400 text-sm max-w-sm mx-auto">
                      Any new invoice you start creating will automatically save here as you type, so you never lose your progress.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingDrafts.map((draft) => {
                    const subtotal = calculateSubtotal(draft.formData.items);
                    const vatAmount = calculateVatAmount(draft.formData.items, vatRate);
                    const total = subtotal + vatAmount;
                    const itemsCount = draft.formData.items.filter((it: any) => it.description || it.unitPrice).length;

                    return (
                      <div
                        key={draft.id}
                        onClick={() => resumeDraft(draft)}
                        className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-blue-100 hover:shadow-xl hover:shadow-blue-900/5 transition-all cursor-pointer"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-black text-slate-900 text-lg group-hover:text-blue-800 transition-colors">
                              {draft.clientName}
                            </span>
                            <span className={cn(
                              "text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider",
                              draft.type === 'proforma' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                            )}>
                              {draft.type === 'proforma' ? 'Proforma' : 'Tax'}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 font-bold">
                            <span className="flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5" />
                              #{draft.invoiceNumber}
                            </span>
                            <span>•</span>
                            <span>
                              {itemsCount} item{itemsCount !== 1 ? 's' : ''}
                            </span>
                            <span>•</span>
                            <span>
                              Edited {new Date(draft.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({new Date(draft.updatedAt).toLocaleDateString()})
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-4 border-t sm:border-t-0 pt-3 sm:pt-0 border-slate-100">
                          <div className="text-right">
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Total Value</p>
                            <p className="font-black text-slate-900 text-base">
                              {draft.formData.items[0]?.currency || defaultCurrency} {total.toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                resumeDraft(draft);
                              }}
                              className="p-2 bg-blue-50 text-blue-800 rounded-xl hover:bg-blue-800 hover:text-white transition-all cursor-pointer"
                              title="Resume drafting"
                            >
                              <Undo className="w-5 h-5" />
                            </button>
                            <button
                              onClick={(e) => deleteDraft(draft.id, e)}
                              className="p-2 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white transition-all cursor-pointer"
                              title="Remove from Pending Box"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <SuccessPopup
        isOpen={!!invoiceSuccessMessage}
        message={invoiceSuccessMessage}
        onClose={() => setInvoiceSuccessMessage(null)}
        title="Invoice Saved"
      />
    </div>
  );
};

export default InvoicesPage;
